import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  Select,
  MenuItem,
  Menu,
  CircularProgress,
  Switch,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Checkbox,
  ListItemText,
} from '@mui/material';
import {
  Save as SaveIcon,
  Close as CloseIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material';
import { styled, useTheme, alpha } from '@mui/material/styles';
import { BglPrimaryButton } from 'components/button/BglButtons';
import { PermissionGate } from 'components/access/PermissionGate';
import { UI_PAGES } from 'framework/access/UiRegistry';
import { useGlobalConfig } from 'context/GlobalContext';
import { ANALYTICS_EVENTS } from 'framework/analytics/AnalyticsEvents';
import { ROUTE_TREE } from 'routes/AppRoutes';
import {
  ArrowLeft,
  Plus,
  ChevronDown,
  X,
  Lock,
  BarChart3,
  Layers,
  Download,
  AlertTriangle,

} from 'lucide-react';
import { useSelector } from 'react-redux';
import { selectPilotList, selectSelectedPilotId } from 'store/dataReducers/pilotsSlice';
import { ClusterApis } from 'apis/clusterAPI';
import { PilotApis } from 'apis/pilotAPI';
import { DCXPromotionApis } from 'apis/DCXPromotionAPI';
import type { StageSheetRows } from 'types/apis/dcxPromotionInterfaces';
import type { ProjectEnvironment } from 'types/apis/pilotInterfaces';
import { projectEnvironmentToAppEnv, getImportSourceAppEnvs, formatAppEnvLabel } from 'features/contentManagement/mediaContent/mediaEnvMapping';
import { featureIdEvent, featureIdFuel, featureIdImportUnit, resolveFeatureDisplayName, titleCaseSegment, withFuelLabel } from 'utils/feature-id';
import { useToast } from 'context/ToastContext';
import { 
  AccessButton, 
  AccessStatusDropdown, 
  getStatusIcon, 
  AccessStatus 
} from 'components/common/AccessStatusButton';

// Recognized fuel type suffixes in featureId (CDF etc. are sub-products, NOT fuel types)
const FUEL_TYPE_SUFFIXES = ['ELECTRIC', 'GAS', 'WATER'];

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.2s ease-in-out',
}));

// Section item interface
interface SectionItem {
  id: string;
  sectionName: string;
  /** The API's own display name for the row. Rendered instead of the raw featureId. */
  displayName?: string;
  position: number;
  mandatory: boolean;
  isRemoved: boolean;
  clusterResolutions: { [clusterReference: string]: AccessStatus };
  mappedPage?: string; // Dashboard page categorization from metadata.mappedPage
  /** The id this row is staged under server-side. Toggling `mandatory` renames the section
   *  locally (the flag lives inside the id), but a stage call has to address the row as it
   *  exists today — sending the renamed id stages a feature that doesn't exist and 404s. */
  stagedId?: string;
}

// Feature group interface
interface FeatureGroup {
  id: string;
  name: string;
  displayName: string;
  sections: SectionItem[];
}

/** Row label — the API's display name, corrected for the featureId shapes it gets wrong
 *  (usage-mode dashboard rows, names that are just a fuel). Shared with the DCX compare table;
 *  the raw featureId stays in the tooltip. */
const sectionLabel = (section: SectionItem): string =>
  resolveFeatureDisplayName(section.displayName, section.sectionName);

/** Env label for stage/error toasts — "Non-Prod (Sandbox)" style: name plus its stack. */
const envLabel = (env: ProjectEnvironment): string => {
  const name = String(env.environment_name ?? env.name ?? env.id);
  return env.stack_name ? `${name} (${env.stack_name})` : name;
};

export const TemplatePersonalizationPage: React.FC = () => {
  const { rc } = useGlobalConfig();
  const theme = useTheme();
  const toast = useToast();
  const selectedPilotId = useSelector(selectSelectedPilotId);
  const pilotList = useSelector(selectPilotList) as any[];
  const selectedPilot = React.useMemo(
    () => pilotList.find((p) => p?.projectId === selectedPilotId),
    [pilotList, selectedPilotId]
  );

  // Product type options
  type ProductType = 'EMAIL' | 'DASHBOARD' | 'HER';
  
  // State
  const [featureGroups, setFeatureGroups] = useState<FeatureGroup[]>([]);
  const [sections, setSections] = useState<SectionItem[]>([]);
  const [removedSections, setRemovedSections] = useState<SectionItem[]>([]);
  const [clusterColumns, setClusterColumns] = useState<string[]>([]);
  const [clustersPerProduct, setClustersPerProduct] = useState<Record<string, string[]>>({}); // Map product -> clusters
  const [availableClusters, setAvailableClusters] = useState<{ clusterReference: string; clusterName: string; clusterId: string }[]>([]);
  const [selectedFeature, setSelectedFeature] = useState<string>('');
  const [selectedProductType, setSelectedProductType] = useState<ProductType>('EMAIL');
  const [productTypeOptions, setProductTypeOptions] = useState<{ value: string; label: string; productType: ProductType }[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dropdownAnchor, setDropdownAnchor] = useState<{ [key: string]: HTMLElement | null }>({});
  const [addClusterAnchor, setAddClusterAnchor] = useState<null | HTMLElement>(null);
  const [addSectionDialogOpen, setAddSectionDialogOpen] = useState(false);
  const [masterSectionList, setMasterSectionList] = useState<SectionItem[]>([]);
  const [schemaLevel2Options, setSchemaLevel2Options] = useState<string[]>([]);
  const [selectedMasterSection, setSelectedMasterSection] = useState<string>('');
  const [selectedRemovedSection, setSelectedRemovedSection] = useState<string>('');
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [dragOverItem, setDragOverItem] = useState<string | null>(null);
  // Track newly added EMAIL sections for push-new-sections API
  // Map of event (e.g., MONTHLY_SUMMARY) -> Set of section names (e.g., HEADER_V4)
  const [newEmailSections, setNewEmailSections] = useState<Map<string, Set<string>>>(new Map());
  
  // Track original state to detect changes
  const [originalSections, setOriginalSections] = useState<SectionItem[]>([]);
  const [originalClustersPerProduct, setOriginalClustersPerProduct] = useState<Record<string, string[]>>({});
  
  const addClusterOpen = Boolean(addClusterAnchor);

  // Environments this project is associated with — used to classify the current one, and as
  // the target choices for the granular event import below.
  const [environments, setEnvironments] = useState<ProjectEnvironment[]>([]);
  const currentEnvId = selectedPilot?.environment_id ? String(selectedPilot.environment_id) : '';
  const currentEnv = environments.find((e) => String(e.id) === currentEnvId);
  /** nonprod/prod targets can't be written to directly — Save Changes becomes Stage Changes,
   *  which edits the already-staged matrix as one batch (see DCXPromotionApis.stageSheetRows). */
  const currentAppEnv = currentEnv ? projectEnvironmentToAppEnv(currentEnv) : null;
  const isStageOnlyEnv = currentAppEnv === 'nonprod' || currentAppEnv === 'prod';

  /** Import selected events (L1 items) from another environment into this one's L2 sheet —
   *  the target is always this project's current environment; only the chosen events' rows are
   *  replaced here (everything else is left untouched). See DCXPromotionApis.importSheets. */
  const [importOpen, setImportOpen] = useState(false);
  const [importSourceEnvId, setImportSourceEnvId] = useState('');
  const [importSelectedEvents, setImportSelectedEvents] = useState<string[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  /** Bumped after an import so the grid and the staged overlay both reload. */
  const [reloadKey, setReloadKey] = useState(0);
  /**
   * Import sources, per the promotion matrix (PROMOTION_TARGETS, read backwards). Only the
   * staged environments are constrained: nonprod and prod may be fed from UAT alone. Anywhere
   * else keeps every associated environment as a candidate.
   */
  const importSourceOptions = environments.filter((env) => {
    if (String(env.id) === currentEnvId) return false;
    if (!isStageOnlyEnv || !currentAppEnv) return true;
    const sourceAppEnv = projectEnvironmentToAppEnv(env);
    return !!sourceAppEnv && getImportSourceAppEnvs(currentAppEnv).includes(sourceAppEnv);
  });

  /** Sources the matrix allows but the pilot has no environment for. Listed disabled rather
   *  than omitted, so a short (or empty) dropdown explains itself. */
  const unmappedSourceAppEnvs = (currentAppEnv ? getImportSourceAppEnvs(currentAppEnv) : []).filter(
    (appEnv) => !environments.some((env) => projectEnvironmentToAppEnv(env) === appEnv)
  );
  /** LEVEL_1 featureIds — the actual source of truth for "Events to Import" below, fetched
   *  separately from the LEVEL_2 `sections` this page's own grid uses. */
  const [level1FeatureIds, setLevel1FeatureIds] = useState<string[]>([]);
  /** LEVEL_2 featureIds at the same source env — the availability filter for the list above. */
  const [sourceL2FeatureIds, setSourceL2FeatureIds] = useState<string[]>([]);
  const [loadingSourceEvents, setLoadingSourceEvents] = useState(false);

  /** `${featureId}::${clusterReference}` keys (API casing, "DEFAULT" not "(Default)") currently
   *  in the target's staged matrix — drives the purple "staged" cell color. */
  const [stagedKeys, setStagedKeys] = useState<Set<string>>(new Set());
  const isCellStaged = (featureId: string, clusterRef: string): boolean =>
    stagedKeys.has(`${featureId}::${clusterRef === '(Default)' ? 'DEFAULT' : clusterRef}`);

  // Check if current selection is Dashboard or HER
  const isDashboard = selectedProductType === 'DASHBOARD';
  const isHER = selectedProductType === 'HER';

  // Track page load on mount
  useEffect(() => {
    rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_TOOLS.TEMPLATE_PERSONALIZATION_LOADED)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Fetch template personalization data on mount (LEVEL_1)
  useEffect(() => {
    const fetchTemplateData = async () => {
      if (!selectedPilotId) {
        return;
      }

      try {
        setLoading(true);
        const response = await ClusterApis.getFeatureMetric(rc, selectedPilotId.toString(), 'LEVEL_2');
        
        if (response.success && response.data) {
          const responseData = response.data as any;
          const rawData = responseData.features || responseData.data || responseData;
          
          if (Array.isArray(rawData)) {
            // Build product type options from the data
            const emailProducts = new Map<string, Set<string>>(); // productKey -> Set of fuel types
            const dashboardProducts = new Map<string, Set<string>>(); // productKey -> Set of fuel types
            const productClusters = new Map<string, Set<string>>(); // productKey -> Set of clusters
            let hasDashboard = false;
            let hasHER = false;

            // Group data by feature prefix and build sections
            const featureMap = new Map<string, FeatureGroup>();
            const sectionsList: SectionItem[] = [];
            
            rawData.forEach((item: any, index: number) => {
              const featureId = item.featureId || item.feature_id || '';
              const featureName = item.featureName || item.feature_name || item.name || '';
              const clusterRef = item.clusterReference || item.cluster_reference || '';
              const resolution = (item.resolution || 'pass').toLowerCase() as 'access' | 'pass' | 'kill';
              const apiPosition = item.position || item.pos || 0; // Get position from API
              const metadata = item.metadata || {};
              const mappedPage = metadata.mappedPage || metadata.mapped_page || '';
              
              // Parse feature ID to get feature group (e.g., "EMAIL.SF.MONTHLY_SUMMARY" from "EMAIL.SF.MONTHLY_SUMMARY.HEADER_V4.FALSE.ELECTRIC")
              const parts = featureId.split('.');
              const productType = parts[0]?.toUpperCase();
              const featureGroupKey = parts.slice(0, 3).join('.');
              const fuelType = parts[parts.length - 1]; // Last part is fuel type (ELECTRIC, GAS, WATER)
              
              // Determine the product key for cluster mapping
              let productKey = featureGroupKey;
              if (productType === 'DASHBOARD') {
                productKey = 'DASHBOARD';
              } else if (productType === 'HER') {
                productKey = 'HER';
              }
              
              // Track clusters per product
              if (!productClusters.has(productKey)) {
                productClusters.set(productKey, new Set(['(Default)'])); // Always include Default
              }
              // Add cluster reference, but skip if it's a variant of "default" (already added)
              if (clusterRef && clusterRef.toUpperCase() !== 'DEFAULT') {
                productClusters.get(productKey)!.add(clusterRef);
              }
              
              // Track product types
              if (productType === 'EMAIL') {
                if (!emailProducts.has(productKey)) {
                  emailProducts.set(productKey, new Set());
                }
                if (FUEL_TYPE_SUFFIXES.includes(fuelType)) {
                  emailProducts.get(productKey)!.add(fuelType);
                }
              } else if (productType === 'DASHBOARD') {
                hasDashboard = true;
                if (!dashboardProducts.has(productKey)) {
                  dashboardProducts.set(productKey, new Set());
                }
                if (FUEL_TYPE_SUFFIXES.includes(fuelType)) {
                  dashboardProducts.get(productKey)!.add(fuelType);
                }
              } else if (productType === 'HER') {
                hasHER = true;
              }
              
              // Find or create section
              let existingSection = sectionsList.find(s => s.sectionName === featureId);
              
              if (!existingSection) {
                existingSection = {
                  id: featureId,
                  sectionName: featureId,
                  displayName: featureName,
                  position: apiPosition, // Use position from API
                  mandatory: parts.includes('TRUE'),
                  isRemoved: false,
                  clusterResolutions: {
                    '(Default)': 'access' // Default column
                  },
                  mappedPage: mappedPage || undefined,
                };
                sectionsList.push(existingSection);
              } else {
                // Update position if this item has a valid position and section doesn't have one yet
                if (apiPosition > 0 && existingSection.position === 0) {
                  existingSection.position = apiPosition;
                }
                // Update mappedPage if not already set
                if (mappedPage && !existingSection.mappedPage) {
                  existingSection.mappedPage = mappedPage;
                }
              }
              
              // Update cluster resolution
              if (clusterRef) {
                // Normalize "DEFAULT" to "(Default)"
                const normalizedClusterRef = clusterRef.toUpperCase() === 'DEFAULT' ? '(Default)' : clusterRef;
                existingSection.clusterResolutions[normalizedClusterRef] = resolution;
              }
              
              // Group by feature
              if (!featureMap.has(featureGroupKey)) {
                featureMap.set(featureGroupKey, {
                  id: featureGroupKey,
                  name: featureGroupKey,
                  displayName: getFeatureDisplayName(featureGroupKey),
                  sections: []
                });
              }
            });
            
            // Sort sections by position from API
            sectionsList.sort((a, b) => a.position - b.position);
            
            // Convert productClusters Map to object for state
            // For EMAIL products, we'll create fuel-specific keys
            const clustersPerProductObj: Record<string, string[]> = {};
            productClusters.forEach((clusters, key) => {
              // Ensure (Default) is always first
              const clusterArray = Array.from(clusters);
              const defaultIndex = clusterArray.indexOf('(Default)');
              if (defaultIndex > 0) {
                clusterArray.splice(defaultIndex, 1);
                clusterArray.unshift('(Default)');
              }
              
              // For EMAIL products, create separate entries for each fuel type
              if (key.startsWith('EMAIL.')) {
                const fuelTypes = emailProducts.get(key);
                if (fuelTypes && fuelTypes.size > 0) {
                  fuelTypes.forEach(fuelType => {
                    const fuelSpecificKey = `${key}.${fuelType}`;
                    clustersPerProductObj[fuelSpecificKey] = clusterArray;
                  });
                } else {
                  // No fuel type variants (e.g., CDF products) - use product key directly
                  clustersPerProductObj[key] = clusterArray;
                }
              } else if (key === 'DASHBOARD') {
                // For DASHBOARD, create separate entries for each fuel type
                const fuelTypes = dashboardProducts.get(key);
                if (fuelTypes) {
                  fuelTypes.forEach(fuelType => {
                    const fuelSpecificKey = `${key}.${fuelType}`;
                    clustersPerProductObj[fuelSpecificKey] = clusterArray;
                  });
                }
              } else {
                // For HER, use the key as-is
                clustersPerProductObj[key] = clusterArray;
              }
            });
            setClustersPerProduct(clustersPerProductObj);
            
            // Build product type dropdown options
            const options: { value: string; label: string; productType: ProductType }[] = [];
            
            // Add Email products - separate menu item for each fuel type
            emailProducts.forEach((fuelTypes, productKey) => {
              const displayName = getFeatureDisplayName(productKey);
              if (fuelTypes.size === 0) {
                // No fuel type variants (e.g., CDF products) - add single option
                options.push({
                  value: productKey,
                  label: displayName,
                  productType: 'EMAIL'
                });
              } else {
                fuelTypes.forEach(fuelType => {
                  const fuelTypeLabel = (() => {
                    switch(fuelType) {
                      case 'ELECTRIC': return 'Electric';
                      case 'GAS': return 'Gas';
                      case 'WATER': return 'Water';
                      default: return fuelType;
                    }
                  })();
                  const fuelSpecificKey = `${productKey}.${fuelType}`;
                  options.push({
                    value: fuelSpecificKey,
                    label: `${displayName} (${fuelTypeLabel})`,
                    productType: 'EMAIL'
                  });
                });
              }
            });
            
            // Add Dashboard - separate menu item for each fuel type
            if (hasDashboard) {
              const dashboardFuelTypes = dashboardProducts.get('DASHBOARD');
              if (dashboardFuelTypes && dashboardFuelTypes.size > 0) {
                dashboardFuelTypes.forEach(fuelType => {
                  const fuelTypeLabel = (() => {
                    switch(fuelType) {
                      case 'ELECTRIC': return 'Electric';
                      case 'GAS': return 'Gas';
                      case 'WATER': return 'Water';
                      default: return fuelType;
                    }
                  })();
                  const fuelSpecificKey = `DASHBOARD.${fuelType}`;
                  options.push({
                    value: fuelSpecificKey,
                    label: `DASHBOARD (${fuelTypeLabel})`,
                    productType: 'DASHBOARD'
                  });
                });
              } else {
                // Fallback: if no fuel types tracked, add single DASHBOARD option
                options.push({
                  value: 'DASHBOARD',
                  label: 'DASHBOARD',
                  productType: 'DASHBOARD'
                });
              }
            }
            
            // Add HER if present
            if (hasHER) {
              options.push({
                value: 'HER',
                label: 'HER',
                productType: 'HER'
              });
            }
            
            setProductTypeOptions(options);
            setSections(sectionsList);
            setMasterSectionList(sectionsList.map(s => ({ ...s })));
            setFeatureGroups(Array.from(featureMap.values()));
            
            // Store original state for change detection
            setOriginalSections(sectionsList.map(s => ({ ...s })));
            setOriginalClustersPerProduct({ ...clustersPerProductObj });
            // The grid now matches the server, so any prior edits are gone with it — e.g. a
            // reload triggered by an import, which replaced the matrix outright.
            setHasChanges(false);
            
            // Set default selected feature and its clusters
            if (options.length > 0) {
              const firstOption = options[0];
              setSelectedFeature(firstOption.value);
              setSelectedProductType(firstOption.productType);
              
              // Set cluster columns for the first product
              const firstProductClusters = clustersPerProductObj[firstOption.value] || ['(Default)'];
              setClusterColumns(firstProductClusters);
            }
          }
        } else {
          const errorMessage = typeof response.error === 'object' 
            ? (response.error?.message || JSON.stringify(response.error)) 
            : (response.error || 'Failed to fetch template data');
          toast.error(errorMessage);
        }
      } catch (error) {
        rc.error(rc.getName(TemplatePersonalizationPage), 'Error fetching template data:', error);
        toast.error('Failed to fetch template data');
      } finally {
        setLoading(false);
      }
    };

    fetchTemplateData();
  }, [rc, selectedPilotId, reloadKey]);

  // Fetch available clusters for the Add Cluster dropdown
  useEffect(() => {
    const fetchClusters = async () => {
      if (!selectedPilotId) {
        return;
      }

      try {
        const response = await ClusterApis.getClustersList(rc, selectedPilotId.toString(), 'TEMPLATE');
        
        if (response.success && response.data) {
          const clusters = response.data.map((cluster: any) => ({
            clusterId: cluster.cluster_id || cluster.clusterId || '',
            clusterName: cluster.cluster_name || cluster.clusterName || '',
            clusterReference: cluster.cluster_reference || cluster.clusterReference || '',
          }));
          setAvailableClusters(clusters);
        }
      } catch (error) {
        rc.error(rc.getName(TemplatePersonalizationPage), 'Error fetching clusters:', error);
      }
    };

    fetchClusters();
  }, [rc, selectedPilotId]);

  // Environments this project is associated with — used only to classify the current one.
  useEffect(() => {
    const fetchEnvironments = async () => {
      if (!selectedPilotId) return;
      try {
        const resp = await PilotApis.getAssociatedEnvironments(rc, String(selectedPilotId));
        setEnvironments(resp?.error ? [] : resp?.data?.data || []);
      } catch (error) {
        rc.error(rc.getName(TemplatePersonalizationPage), 'Error fetching associated environments:', error);
        setEnvironments([]);
      }
    };

    fetchEnvironments();
  }, [rc, selectedPilotId]);

  /**
   * Both levels at the CHOSEN SOURCE ENV, for the "Events to Import" dropdown: LEVEL_1 gives
   * the candidate events, LEVEL_2 says which of them can actually be imported. An event with
   * no LEVEL_2 rows at the source imports nothing and comes back as eventsNotFound.
   *
   * Read from the source env, not this one — the compare endpoint's current_env is the same
   * env the import reads from — and refetched whenever the pilot or source env changes, since
   * the same event is present in one environment and absent in another.
   */
  useEffect(() => {
    const fetchSourceEvents = async () => {
      if (!importOpen || !selectedPilotId || !importSourceEnvId || !currentEnvId) {
        setLevel1FeatureIds([]);
        setSourceL2FeatureIds([]);
        return;
      }
      setLoadingSourceEvents(true);
      const ids = (resp: any): string[] =>
        resp?.success && resp.data ? resp.data.data.map((row: any) => row.featureId).filter(Boolean) : [];
      try {
        const [l1, l2] = await Promise.all([
          DCXPromotionApis.compareSheets(rc, String(selectedPilotId), importSourceEnvId, currentEnvId, 'LEVEL_1'),
          DCXPromotionApis.compareSheets(rc, String(selectedPilotId), importSourceEnvId, currentEnvId, 'LEVEL_2'),
        ]);
        setLevel1FeatureIds(ids(l1));
        setSourceL2FeatureIds(ids(l2));
      } catch (error) {
        rc.error(rc.getName(TemplatePersonalizationPage), 'Error fetching source events:', error);
        setLevel1FeatureIds([]);
        setSourceL2FeatureIds([]);
      } finally {
        setLoadingSourceEvents(false);
      }
    };

    fetchSourceEvents();
  }, [rc, importOpen, selectedPilotId, importSourceEnvId, currentEnvId]);

  /** Refetch the staged matrix for the current target — only meaningful for nonprod/prod, so
   *  direct-write targets just clear it. Re-run after a successful Stage Changes too. */
  const refreshStagedKeys = useCallback(async () => {
    if (!isStageOnlyEnv || !currentEnvId || !selectedPilotId) {
      setStagedKeys(new Set());
      return;
    }
    const resp = await DCXPromotionApis.getStagedSheets(rc, {
      target_env: currentEnvId,
      project_id: selectedPilotId.toString(),
      level: 'LEVEL_2',
    });
    if (resp.success && resp.data) {
      setStagedKeys(new Set(resp.data.data.map((row) => `${row.featureId}::${row.clusterReference}`)));
    }
  }, [rc, currentEnvId, isStageOnlyEnv, selectedPilotId]);

  useEffect(() => {
    refreshStagedKeys();
  }, [refreshStagedKeys, reloadKey]);

  // Fetch feature metric schema for override section dropdown (email level2)
  useEffect(() => {
    const fetchFeatureMetricSchema = async () => {
      if (!selectedPilotId) {
        return;
      }

      try {
        const response = await ClusterApis.getFeatureMetricSchema(rc, selectedPilotId.toString());
        
        if (response.success && response.data) {
          const schemaData = response.data;
          // Extract email level2 FCMs for the override section dropdown
          if (schemaData.email?.fcms?.level2) {
            setSchemaLevel2Options(schemaData.email.fcms.level2);
          }
        }
      } catch (error) {
        rc.error(rc.getName(TemplatePersonalizationPage), 'Error fetching feature metric schema:', error);
      }
    };

    fetchFeatureMetricSchema();
  }, [rc, selectedPilotId]);

  const getFeatureDisplayName = (featureId: string): string => {
    // Convert feature ID to display name
    // Handle fuel-specific keys (e.g., EMAIL.SF.MONTHLY_SUMMARY.ELECTRIC)
    const parts = featureId.split('.');
    // Remove fuel type if it's the last part (ELECTRIC, GAS, WATER)
    const filteredParts = parts.length > 0 && FUEL_TYPE_SUFFIXES.includes(parts[parts.length - 1])
      ? parts.slice(0, -1)
      : parts;
    
    if (filteredParts.length >= 3) {
      const name = filteredParts[2].replace(/_/g, ' ');
      return `(${filteredParts[1] || 'EL'}) ${name.charAt(0).toUpperCase() + name.slice(1).toLowerCase()}`;
    }
    return featureId;
  };

  /** One option per (event, fuel) — the same event exists separately per fuel and imports
   *  separately, so "Bill Projection (EL)" and "Bill Projection (GAS)" are distinct picks. */
  const importEventKey = (event: string, fuel: string | null) => `${event}::${fuel ?? ''}`;

  /** The (event, fuel) pairs the source env actually has LEVEL_2 rows for. An L1 event missing
   *  from here imports nothing, so it never reaches the dropdown. Keyed per fuel because an
   *  event can have L2 rows for one fuel and none for another. */
  const importableL2Keys = React.useMemo(() => {
    const keys = new Set<string>();  // unit::fuel
    const units = new Set<string>(); // unit, whatever fuel
    sourceL2FeatureIds.forEach((featureId) => {
      const unit = featureIdImportUnit(featureId);
      if (!unit) return;
      keys.add(importEventKey(unit, featureIdFuel(featureId)));
      units.add(unit);
    });
    return { keys, units };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceL2FeatureIds]);

  /**
   * Can this LEVEL_1 entry be imported — i.e. does the source have LEVEL_2 rows for it?
   *
   * A fuel-scoped entry needs its own fuel present (BILL_PROJECTION exists at L2 for ELECTRIC
   * but not WATER). A fuel-agnostic one (`EMAIL.DF.USER_WELCOME`, no fuel segment) matches any
   * fuel: its L2 rows ARE fuel-scoped (`…USER_WELCOME.TEASER.TRUE.WATER`), so demanding an
   * exact fuel match would hide it entirely.
   */
  const hasImportableL2 = (featureId: string): boolean => {
    const unit = featureIdImportUnit(featureId);
    const fuel = featureIdFuel(featureId);
    return fuel ? importableL2Keys.keys.has(importEventKey(unit, fuel)) : importableL2Keys.units.has(unit);
  };

  /** Unique LEVEL_1 events available to import into another environment's L2 sheet — EMAIL and
   *  DASHBOARD only, so HER and SMS are excluded (HER's LEVEL_2 is .xlsx-only and never
   *  uploaded; SMS has no L2 sheet to import into either), and only those the source actually
   *  has LEVEL_2 rows for. */
  const importableEvents = React.useMemo(() => {
    const byKey = new Map<string, { value: string; label: string; event: string; fuel: string | null }>();
    level1FeatureIds.forEach((featureId) => {
      const product = featureId.split('.')[0]?.toUpperCase();
      if (product !== 'EMAIL' && product !== 'DASHBOARD') return;
      const event = featureIdEvent(featureId);
      if (!event) return;
      const fuel = featureIdFuel(featureId);
      const value = importEventKey(featureIdImportUnit(featureId), fuel);
      if (byKey.has(value) || !hasImportableL2(featureId)) return;
      const name = titleCaseSegment(event);
      byKey.set(value, {
        value,
        label: withFuelLabel(name, fuel),
        event,
        fuel,
      });
    });
    return Array.from(byKey.values()).sort((a, b) => a.label.localeCompare(b.label));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level1FeatureIds, importableL2Keys]);

  /** Looks up a label for either a bare event token or one of the full-feature-id selectors
   *  actually sent to the API — the latter is what a "not found at source" response echoes back. */
  const importEventLabel = (selector: string): string => {
    const event = featureIdEvent(selector) || selector;
    return importableEvents.find((e) => e.event === event)?.label || selector;
  };

  /** Option key -> the LEVEL_1 featureIds behind it (one per sub-brand). These full ids are
   *  what goes to the import API: the trailing fuel segment scopes the import to that fuel,
   *  and the leading segments pin the product and sub-brand (see sheet_selector_matches). */
  const eventSelectorMap = React.useMemo(() => {
    const map = new Map<string, Set<string>>();
    level1FeatureIds.forEach((featureId) => {
      const product = featureId.split('.')[0]?.toUpperCase();
      if (product !== 'EMAIL' && product !== 'DASHBOARD') return;
      const key = importEventKey(featureIdImportUnit(featureId), featureIdFuel(featureId));
      if (!hasImportableL2(featureId)) return;
      if (!map.has(key)) map.set(key, new Set());
      map.get(key)!.add(featureId);
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level1FeatureIds, importableL2Keys]);

  /** Pull the selected events' L2 rows from another associated environment into this project's
   *  current one. Merges over this environment — events not selected are left as-is here
   *  (see DCXPromotionApis.importSheets). */
  const handleImportEvents = async () => {
    if (!selectedPilotId || !currentEnvId || !importSourceEnvId || importSelectedEvents.length === 0) {
      return;
    }

    // Full LEVEL_1 featureIds, one per sub-brand behind each pick — the trailing fuel segment
    // is what keeps "Bill Projection (EL)" from dragging in the gas rows too.
    const events = importSelectedEvents.flatMap((value) =>
      Array.from(eventSelectorMap.get(value) ?? [value.split('::')[0]])
    );

    setIsImporting(true);
    const resp = await DCXPromotionApis.importSheets(rc, {
      project_id: String(selectedPilotId),
      source_env: importSourceEnvId,
      target_env: currentEnvId,
      level: 'LEVEL_2',
      events,
    });
    setIsImporting(false);

    if (!resp.success || !resp.data) {
      toast.error(typeof resp.error === 'string' ? resp.error : resp.error?.message || 'Failed to import the selected events');
      return;
    }

    const { mode, rows = 0, eventsNotFound = [], failed = 0 } = resp.data;
    const targetLabel = currentEnv ? envLabel(currentEnv) : 'this environment';
    const eventCount = importSelectedEvents.length;

    setImportOpen(false);
    setImportSelectedEvents([]);

    if (mode === 'staged') {
      toast.success(`Staged ${rows} row(s) for ${eventCount} event(s) into ${targetLabel} — nothing written yet.`);
    } else if (failed > 0) {
      toast.error(`Imported with ${failed} file(s) failing for ${targetLabel}.`);
    } else {
      toast.success(`Imported ${rows} row(s) for ${eventCount} event(s) into ${targetLabel}.`);
    }

    if (eventsNotFound.length > 0) {
      toast.warning(`Not found at source: ${eventsNotFound.map(importEventLabel).join(', ')}`);
    }

    // The grid below is now stale either way: an applied import rewrites the live matrix, and
    // a staged one changes both what the feature-metric endpoint serves for nonprod/prod
    // (staged overrides live) and which cells come back staged.
    setReloadKey((k) => k + 1);
  };

  const handleBack = () => {
    rc.uiRouter.navigate(ROUTE_TREE.CONFIG_TOOLS.DCX_CLUSTERS);
  };

  const handleAccessChange = (sectionId: string, clusterReference: string, newStatus: 'access' | 'pass' | 'kill') => {
    // Prevent setting 'pass' for default cluster
    const isDefaultCluster = clusterReference === '(Default)';
    const finalStatus = (isDefaultCluster && newStatus === 'pass') ? 'access' : newStatus;

    rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_TOOLS.TEMPLATE_RULE_EDITED, {
      product_type: selectedProductType,
    });

    setSections(prev => prev.map(section => 
      section.id === sectionId 
        ? { 
            ...section, 
            clusterResolutions: { 
              ...section.clusterResolutions, 
              [clusterReference]: finalStatus 
            } 
          }
        : section
    ));
    setHasChanges(true);
  };

  const handleMandatoryToggle = (sectionId: string) => {
    setSections(prev => prev.map(section => {
      if (section.id === sectionId) {
        const newMandatory = !section.mandatory;
        // Update the section name - change the second-to-last param (TRUE/FALSE)
        const parts = section.sectionName.split('.');
        if (parts.length >= 2) {
          // Second to last position is the mandatory flag
          parts[parts.length - 2] = newMandatory ? 'TRUE' : 'FALSE';
        }
        const newSectionName = parts.join('.');
        return {
          ...section,
          mandatory: newMandatory,
          sectionName: newSectionName,
          id: newSectionName, // Update id as well since it's based on section name
          // Remember what the row is still staged under — the server computes the rename.
          stagedId: section.stagedId ?? section.id,
        };
      }
      return section;
    }));
    setHasChanges(true);
  };

  const handleRemoveSection = (sectionId: string) => {
    const sectionToRemove = sections.find(s => s.id === sectionId);
    if (sectionToRemove) {
      setSections(prev => prev.filter(s => s.id !== sectionId));
      setRemovedSections(prev => [...prev, { ...sectionToRemove, isRemoved: true }]);
      rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_TOOLS.TEMPLATE_RULE_DELETED, {
        product_type: selectedProductType,
      });
      setHasChanges(true);
    }
  };

  const handleReAddSection = () => {
    if (!selectedRemovedSection) return;
    
    const sectionToAdd = removedSections.find(s => s.id === selectedRemovedSection);
    if (sectionToAdd) {
      setRemovedSections(prev => prev.filter(s => s.id !== selectedRemovedSection));
      setSections(prev => [...prev, { ...sectionToAdd, isRemoved: false, position: prev.length + 1 }]);
      rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_TOOLS.TEMPLATE_RULE_CREATED, {
        product_type: selectedProductType,
        source: 're_add',
      });
      setSelectedRemovedSection('');
      setHasChanges(true);
    }
  };

  const handleDropdownOpen = (event: React.MouseEvent<HTMLElement>, sectionId: string, column: string) => {
    const key = `${sectionId}-${column}`;
    setDropdownAnchor(prev => ({ ...prev, [key]: event.currentTarget }));
  };

  const handleDropdownClose = (sectionId: string, column: string) => {
    const key = `${sectionId}-${column}`;
    setDropdownAnchor(prev => ({ ...prev, [key]: null }));
  };

  // Helper function to detect if a section has changed
  const hasSectionChanged = (currentSection: SectionItem, originalSection: SectionItem | undefined): boolean => {
    // New section (not in original)
    if (!originalSection) {
      return true;
    }
    
    // Check if position changed
    if (currentSection.position !== originalSection.position) {
      return true;
    }
    
    // Check if mandatory changed
    if (currentSection.mandatory !== originalSection.mandatory) {
      return true;
    }
    
    // Check if section name changed (which includes mandatory flag)
    if (currentSection.sectionName !== originalSection.sectionName) {
      return true;
    }
    
    // Check if cluster resolutions changed
    const currentClusters = Object.keys(currentSection.clusterResolutions);
    const originalClusters = Object.keys(originalSection.clusterResolutions);
    
    // Check if clusters were added or removed
    if (currentClusters.length !== originalClusters.length) {
      return true;
    }
    
    // Check if any resolution value changed
    for (const clusterRef of currentClusters) {
      const currentResolution = currentSection.clusterResolutions[clusterRef] || 'pass';
      const originalResolution = originalSection.clusterResolutions[clusterRef] || 'pass';
      if (currentResolution !== originalResolution) {
        return true;
      }
    }
    
    return false;
  };

  // Helper function to detect if clusters changed for a product
  const hasClustersChanged = (productKey: string): boolean => {
    const currentClusters = clustersPerProduct[productKey] || [];
    const originalClusters = originalClustersPerProduct[productKey] || [];
    
    if (currentClusters.length !== originalClusters.length) {
      return true;
    }
    
    // Check if cluster order or membership changed
    const currentSet = new Set(currentClusters);
    const originalSet = new Set(originalClusters);
    
    if (currentSet.size !== originalSet.size) {
      return true;
    }
    
    for (const cluster of currentClusters) {
      if (!originalSet.has(cluster)) {
        return true;
      }
    }
    
    return false;
  };

  // Helper function to get all products that have been modified
  const getModifiedProducts = (): Set<string> => {
    const modifiedProducts = new Set<string>();
    
    // Check sections for changes
    sections.forEach(currentSection => {
      const originalSection = originalSections.find(s => s.id === currentSection.id);
      if (hasSectionChanged(currentSection, originalSection)) {
        // Extract product key from section name
        const parts = currentSection.sectionName.split('.');
        if (parts[0]?.toUpperCase() === 'DASHBOARD') {
          modifiedProducts.add('DASHBOARD');
        } else if (parts.length >= 3) {
          modifiedProducts.add(parts.slice(0, 3).join('.'));
        }
      }
    });
    
    // Check for removed sections
    originalSections.forEach(originalSection => {
      const currentSection = sections.find(s => s.id === originalSection.id);
      if (!currentSection) {
        // Section was removed
        const parts = originalSection.sectionName.split('.');
        if (parts[0]?.toUpperCase() === 'DASHBOARD') {
          modifiedProducts.add('DASHBOARD');
        } else if (parts.length >= 3) {
          modifiedProducts.add(parts.slice(0, 3).join('.'));
        }
      }
    });
    
    // Check for cluster changes per product
    Object.keys(clustersPerProduct).forEach(productKey => {
      if (hasClustersChanged(productKey)) {
        modifiedProducts.add(productKey);
      }
    });
    
    return modifiedProducts;
  };

  const handleSaveChanges = async () => {
    if (!selectedPilotId) {
      toast.error('No project selected');
      return;
    }

    setSaving(true);

    try {
      // Get all modified products
      const modifiedProducts = getModifiedProducts();
      
      if (modifiedProducts.size === 0) {
        toast.warning('No changes detected');
        setSaving(false);
        return;
      }
      
      // Group sections by component (EMAIL, DASHBOARD) based on sectionName prefix
      // Note: HER is excluded from feature resolution for this screen
      const componentGroups: { [key: string]: SectionItem[] } = {
        EMAIL: [],
        DASHBOARD: []
      };

      // Check if any EMAIL product is modified
      const hasAnyEmailProductModified = Array.from(modifiedProducts).some(productKey => 
        productKey.startsWith('EMAIL.')
      );
      // Check if DASHBOARD is modified
      const hasDashboardModified = modifiedProducts.has('DASHBOARD');

      // Include sections based on component-level modification
      // If ANY email product is dirty, include ALL email product sheets
      // If dashboard is dirty, include ALL dashboard sheets
      sections.forEach(section => {
        const prefix = section.sectionName.split('.')[0].toUpperCase();
        if (prefix in componentGroups) {
          if (prefix === 'EMAIL' && hasAnyEmailProductModified) {
            // Include all EMAIL sections if any EMAIL product is modified
            componentGroups[prefix].push(section);
          } else if (prefix === 'DASHBOARD' && hasDashboardModified) {
            // Include all DASHBOARD sections if DASHBOARD is modified
            componentGroups[prefix].push(section);
          }
        }
      });

      // Build files array for each component that has modified sections
      const files: {
        component: 'EMAIL' | 'DASHBOARD';
        level: 'L1' | 'L2';
        headers: string[];
        features: { featureId: string; values: string[] }[];
      }[] = [];

      (['EMAIL', 'DASHBOARD'] as const).forEach(component => {
        const componentSections = componentGroups[component];
        if (componentSections.length > 0) {
          // Get all clusters for ALL products of this component type
          // Since we're including all sections of the component, we need clusters from all products
          const componentClusters = new Set<string>();
          
          // Collect clusters from all products of this component type
          Object.keys(clustersPerProduct).forEach(productKey => {
            const productPrefix = productKey.split('.')[0].toUpperCase();
            if ((component === 'EMAIL' && productPrefix === 'EMAIL') || 
                (component === 'DASHBOARD' && (productKey === 'DASHBOARD' || productKey.startsWith('DASHBOARD.')))) {
              const productClusters = clustersPerProduct[productKey] || ['(Default)'];
              productClusters.forEach(cluster => componentClusters.add(cluster));
            }
          });
          
          // Convert to array and ensure (Default) is first
          const allClusters = Array.from(componentClusters);
          const defaultIndex = allClusters.indexOf('(Default)');
          if (defaultIndex > 0) {
            allClusters.splice(defaultIndex, 1);
            allClusters.unshift('(Default)');
          }
          
          // Build headers array from clusters
          const apiClusterColumns = allClusters.map(c => c === '(Default)' ? 'DEFAULT' : c);
          const headers = ['FEATURE_ID', ...apiClusterColumns, 'POSITION'];
          
          // Sort sections to match UI display order
          let sortedSections: SectionItem[];

          if (component === 'DASHBOARD') {
            // Dashboard: pages first (grouped by fuel type), then features grouped per page (fuel types together)
            const fuelTypeOrder: Record<string, number> = { 'ELECTRIC': 0, 'GAS': 1, 'WATER': 2 };
            const getFuelType = (sectionName: string): string => {
              const parts = sectionName.split('.');
              const lastPart = parts[parts.length - 1];
              return FUEL_TYPE_SUFFIXES.includes(lastPart) ? lastPart : '';
            };

            const dashPageItems: SectionItem[] = [];
            const dashFeatureItems: SectionItem[] = [];

            componentSections.forEach(section => {
              const coreName = getDashboardCoreName(section.sectionName);
              if (section.mappedPage && coreName === section.mappedPage) {
                dashPageItems.push(section);
              } else {
                dashFeatureItems.push(section);
              }
            });

            // Sort pages: by fuel type first (ELECTRIC, GAS, WATER), then by position within each fuel type
            dashPageItems.sort((a, b) => {
              const fuelA = fuelTypeOrder[getFuelType(a.sectionName)] ?? 99;
              const fuelB = fuelTypeOrder[getFuelType(b.sectionName)] ?? 99;
              if (fuelA !== fuelB) return fuelA - fuelB;
              return a.position - b.position;
            });

            // Get unique page names in position order (deduplicated across fuel types)
            const uniquePageNames: string[] = [];
            const seenPages = new Set<string>();
            dashPageItems.forEach(page => {
              const pageName = page.mappedPage || getDashboardCoreName(page.sectionName);
              if (!seenPages.has(pageName)) {
                seenPages.add(pageName);
                uniquePageNames.push(pageName);
              }
            });

            // Build page position map PER FUEL TYPE: within each fuel group, pages get sequential positions
            // e.g. GAS has only HOME(1) and SETTINGS_AND_PREFERENCES(2), not the global position 6
            const pagePositionByFuel = new Map<string, number>(); // key: `fuel::pageName` → position
            const fuelPageGroups = new Map<string, SectionItem[]>();
            dashPageItems.forEach(page => {
              const fuel = getFuelType(page.sectionName);
              if (!fuelPageGroups.has(fuel)) fuelPageGroups.set(fuel, []);
              fuelPageGroups.get(fuel)!.push(page);
            });
            fuelPageGroups.forEach((pages, fuel) => {
              // Pages within each fuel are already sorted by position (dashPageItems was sorted above)
              pages.forEach((page, index) => {
                const pageName = page.mappedPage || getDashboardCoreName(page.sectionName);
                pagePositionByFuel.set(`${fuel}::${pageName}`, index + 1);
              });
            });

            // Build feature position map: per page, each unique feature core name gets a sequential position
            const featurePositionMap = new Map<string, number>(); // key: `pageName::coreName` → position
            uniquePageNames.forEach(pageName => {
              const pageFeatures = dashFeatureItems
                .filter(f => f.mappedPage === pageName)
                .sort((a, b) => a.position - b.position);
              const seenCores = new Set<string>();
              let featurePos = 0;
              pageFeatures.forEach(f => {
                const coreName = getDashboardCoreName(f.sectionName);
                if (!seenCores.has(coreName)) {
                  seenCores.add(coreName);
                  featurePos++;
                  featurePositionMap.set(`${pageName}::${coreName}`, featurePos);
                }
              });
            });

            // Build sorted list: all pages first (grouped by fuel type), with per-fuel positions
            const sortedDashboard: SectionItem[] = dashPageItems.map(section => {
              const fuel = getFuelType(section.sectionName);
              const pageName = section.mappedPage || getDashboardCoreName(section.sectionName);
              const pos = pagePositionByFuel.get(`${fuel}::${pageName}`) || section.position;
              return { ...section, position: pos };
            });

            // Then features: grouped by page name, fuel types together within each page, with correct positions
            uniquePageNames.forEach(pageName => {
              const pageFeatures = dashFeatureItems
                .filter(f => f.mappedPage === pageName)
                .sort((a, b) => {
                  const fuelA = fuelTypeOrder[getFuelType(a.sectionName)] ?? 99;
                  const fuelB = fuelTypeOrder[getFuelType(b.sectionName)] ?? 99;
                  if (fuelA !== fuelB) return fuelA - fuelB;
                  return a.position - b.position;
                })
                .map(f => {
                  const coreName = getDashboardCoreName(f.sectionName);
                  const pos = featurePositionMap.get(`${pageName}::${coreName}`) || f.position;
                  return { ...f, position: pos };
                });
              sortedDashboard.push(...pageFeatures);
            });

            // Append orphan features (not belonging to any page)
            const allPageNames = new Set(uniquePageNames);
            const orphans = dashFeatureItems
              .filter(f => !allPageNames.has(f.mappedPage || ''))
              .sort((a, b) => a.position - b.position)
              .map((f, index) => ({ ...f, position: index + 1 }));
            sortedDashboard.push(...orphans);

            sortedSections = sortedDashboard;
          } else {
            // EMAIL: sort by product (first 3 parts of sectionName) then by position
            sortedSections = [...componentSections].sort((a, b) => {
            const productA = a.sectionName.split('.').slice(0, 3).join('.');
            const productB = b.sectionName.split('.').slice(0, 3).join('.');
            if (productA !== productB) {
              return productA.localeCompare(productB);
            }
            return a.position - b.position;
          });
          }

          files.push({
            component,
            level: 'L2',
            headers,
            features: sortedSections.map(section => ({
              featureId: section.id,
              values: [
                ...allClusters.map(clusterRef => {
                  const resolution = section.clusterResolutions[clusterRef] || 'pass';
                  return resolution.toUpperCase();
                }),
                // Add position value for both EMAIL and DASHBOARD
                section.position.toString()
              ]
            }))
          });
        }
      });

      if (files.length === 0) {
        toast.error('No sections to save');
        setSaving(false);
        return;
      }

      const payload = {
        projectId: selectedPilotId.toString(),
        files
      };

      const response = await ClusterApis.saveFeatureAccess(rc, payload);

      if (response.success && response.data) {
        const responseData = response.data;
        
        // Check if all files were uploaded and API was notified
        const uploadedFiles = responseData.uploaded_files || [];
        const allNotified = uploadedFiles.every((file: any) => file.api_notified);
        
        if (allNotified) {
          toast.success(responseData.message || 'Template configuration saved successfully');
        } else {
          // Some files may have notification errors
          const failedFiles = uploadedFiles.filter((file: any) => !file.api_notified);
          const failedNames = failedFiles.map((f: any) => f.file_name).join(', ');
          toast.warning(`Template configuration saved, but some files failed to notify backend: ${failedNames}`);
        }

        rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_TOOLS.TEMPLATE_RULES_SAVED, {
          modified_product_count: modifiedProducts.size,
          file_count: files.length,
        });

        // Show any additional errors
        if (responseData.errors && responseData.errors.length > 0) {
          responseData.errors.forEach((error: any) => {
            const errorMessage = typeof error === 'object' ? (error.message || JSON.stringify(error)) : error;
            toast.error(errorMessage);
          });
        }

        // Call push-new-sections API if there are new EMAIL sections
        if (newEmailSections.size > 0) {
          const emailPayload: { event: string; sections: string[] }[] = [];
          
          newEmailSections.forEach((sectionsSet, event) => {
            if (sectionsSet.size > 0) {
              emailPayload.push({
                event,
                sections: Array.from(sectionsSet)
              });
            }
          });

          if (emailPayload.length > 0) {
            try {
              const pushResponse = await ClusterApis.pushNewSections(rc, {
                project_id: selectedPilotId.toString(),
                email: emailPayload
              });

              if (pushResponse.success) {
                rc.status(rc.getName(TemplatePersonalizationPage), 'New sections pushed successfully');
              } else {
                rc.warn(rc.getName(TemplatePersonalizationPage), 'Failed to push new sections:', pushResponse.error);
              }
            } catch (pushError) {
              rc.error(rc.getName(TemplatePersonalizationPage), 'Error pushing new sections:', pushError);
            }
          }

          // Clear tracked new sections after save
          setNewEmailSections(new Map());
        }

        // Update original state to reflect the saved state
        setOriginalSections(sections.map(s => ({ ...s })));
        setOriginalClustersPerProduct({ ...clustersPerProduct });
        
        setHasChanges(false);
      } else {
        // Handle error object with code/message structure
        const errorMessage = typeof response.error === 'object' 
          ? (response.error?.message || JSON.stringify(response.error)) 
          : (response.error || 'Failed to save template configuration');
        toast.error(errorMessage);
      }
    } catch (error) {
      rc.error(rc.getName(TemplatePersonalizationPage), 'Error saving template configuration:', error);
      toast.error('Failed to save template configuration');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Stage Changes — nonprod/prod targets. The stage endpoint (PUT .../sheets/stage) takes the
   * whole grid as one batch, keyed by (target_env, project_id, featureId, clusterReference),
   * and is update-only: rows the environment has never staged come back under `notStaged`
   * (Import Sheet on the Feature Access page seeds them) rather than failing the batch.
   *
   * Resolution, position and mandatory all stage. Section add/remove still doesn't — the
   * endpoint cannot create rows — so those edits are skipped here.
   */
  const handleStageChanges = async () => {
    if (!selectedPilotId || !currentEnvId) {
      toast.error('No environment selected');
      return;
    }

    setSaving(true);

    try {
      const rows: StageSheetRows.Row[] = [];
      sections.forEach((section) => {
        // Address the row by the id it's staged under: a mandatory toggle renames the section
        // locally, but the server computes that rename from the current id.
        const stagedId = section.stagedId ?? section.id;
        const original = originalSections.find((s) => s.id === stagedId);
        if (!original) return; // never staged (a newly added section) — nothing to update

        const mandatoryChanged = section.mandatory !== original.mandatory;
        const positionChanged = section.position !== original.position;

        const clusterKeys = new Set([
          ...Object.keys(section.clusterResolutions),
          ...Object.keys(original.clusterResolutions),
        ]);

        clusterKeys.forEach((clusterRef) => {
          const current = section.clusterResolutions[clusterRef] ?? 'pass';
          const before = original.clusterResolutions[clusterRef] ?? 'pass';

          // position and mandatory live on every one of a feature's cluster rows, so a change
          // to either has to go out for all of them — renaming just the addressed row would
          // split the feature across two featureIds.
          if (current === before && !mandatoryChanged && !positionChanged) return;

          rows.push({
            featureId: stagedId,
            clusterReference: clusterRef === '(Default)' ? 'DEFAULT' : clusterRef,
            resolution: current.toUpperCase(),
            ...(positionChanged ? { position: section.position } : {}),
            ...(mandatoryChanged ? { mandatory: section.mandatory } : {}),
          });
        });
      });

      if (rows.length === 0) {
        toast.warning("No stageable changes — adding or removing sections can't be staged for this environment.");
        return;
      }

      const resp = await DCXPromotionApis.stageSheetRows(rc, {
        target_env: currentEnvId,
        project_id: selectedPilotId.toString(),
        rows,
      });

      if (!resp.success || !resp.data) {
        const message = typeof resp.error === 'string' ? resp.error : (resp.error as any)?.message;
        toast.error(message || 'Failed to stage template configuration');
        return;
      }

      const updated = resp.data.rows?.updated?.length ?? 0;
      const notStagedYet = resp.data.rows?.notStaged?.length ?? 0;

      if (updated > 0) {
        rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_TOOLS.TEMPLATE_RULES_SAVED, {
          file_count: updated,
        });
      }

      if (notStagedYet > 0) {
        toast.warning(
          `${notStagedYet} change(s) couldn't be staged — ${currentEnv ? envLabel(currentEnv) : 'this environment'} has no imported matrix yet. Use Import Sheet on the Feature Access page first.`
        );
      }
      if (updated === rows.length) {
        toast.success(`Staged ${updated} change(s) for ${currentEnv ? envLabel(currentEnv) : 'this environment'}.`);
      } else if (updated > 0) {
        toast.success(`Staged ${updated} of ${rows.length} change(s).`);
      }

      // Reload rather than patch: a mandatory change renames rows server-side, so the local
      // ids (and the staged overlay) are stale the moment the batch lands.
      setReloadKey((k) => k + 1);
    } catch (error) {
      rc.error(rc.getName(TemplatePersonalizationPage), 'Error staging template configuration:', error);
      toast.error('Failed to stage template configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleAddClusterClick = (event: React.MouseEvent<HTMLElement>) => {
    setAddClusterAnchor(event.currentTarget);
  };

  const handleAddClusterClose = () => {
    setAddClusterAnchor(null);
  };

  const handleClusterTypeSelect = (clusterReference: string) => {
    if (clusterColumns.includes(clusterReference)) {
      handleAddClusterClose();
      return;
    }

    setClusterColumns(prev => [...prev, clusterReference]);
    
    // Update clustersPerProduct for ALL products of the same component type (EMAIL or DASHBOARD)
    // This ensures the cluster is visible across all products of that type
    setClustersPerProduct(prev => {
      const updated = { ...prev };
      
      // Determine the component type (EMAIL or DASHBOARD)
      const componentType = selectedProductType; // 'EMAIL' or 'DASHBOARD'
      
      if (componentType === 'EMAIL') {
        // For EMAIL, extract the product key (without fuel type) from selectedFeature
        // selectedFeature format: EMAIL.SF.MONTHLY_SUMMARY.ELECTRIC or EMAIL.CDF.NEIGHBOURHOOD_COMPARISON (no fuel type)
        const parts = selectedFeature.split('.');
        const lastPart = parts[parts.length - 1];
        
        if (FUEL_TYPE_SUFFIXES.includes(lastPart)) {
          const productKeyWithoutFuel = parts.slice(0, -1).join('.'); // Everything except last part (fuel type)
          // Update all fuel-specific keys that match this product
          Object.keys(updated).forEach(productKey => {
            if (productKey.startsWith(productKeyWithoutFuel + '.')) {
              updated[productKey] = [...(updated[productKey] || ['(Default)']), clusterReference];
            }
          });
        } else {
          // No fuel type suffix (e.g., CDF products) - update matching keys directly
          Object.keys(updated).forEach(productKey => {
            if (productKey === selectedFeature || productKey.startsWith(selectedFeature + '.')) {
              updated[productKey] = [...(updated[productKey] || ['(Default)']), clusterReference];
            }
          });
        }
      } else if (componentType === 'DASHBOARD') {
        // For DASHBOARD, extract the fuel type from selectedFeature
        // selectedFeature format: DASHBOARD.ELECTRIC
        const parts = selectedFeature.split('.');
        if (parts.length > 1 && parts[0] === 'DASHBOARD') {
          // Update all DASHBOARD fuel-specific keys
          Object.keys(updated).forEach(productKey => {
            if (productKey.startsWith('DASHBOARD.')) {
              updated[productKey] = [...(updated[productKey] || ['(Default)']), clusterReference];
            }
          });
        } else {
          // Fallback: update all DASHBOARD keys
          Object.keys(updated).forEach(productKey => {
            if (productKey === 'DASHBOARD' || productKey.startsWith('DASHBOARD.')) {
              updated[productKey] = [...(updated[productKey] || ['(Default)']), clusterReference];
            }
          });
        }
      } else {
        // For HER, update all products that match the component type
        Object.keys(updated).forEach(productKey => {
          const productPrefix = productKey.split('.')[0].toUpperCase();
          if (productPrefix === componentType || productKey === componentType) {
            updated[productKey] = [...(updated[productKey] || ['(Default)']), clusterReference];
          }
        });
      }
      
      // Also update the current selected feature if not already updated
      if (!updated[selectedFeature]?.includes(clusterReference)) {
        updated[selectedFeature] = [...(updated[selectedFeature] || ['(Default)']), clusterReference];
      }
      
      return updated;
    });

    // Set default 'pass' resolution for all sections of the same component type
    setSections(prev => prev.map(section => {
      const sectionComponentType = section.sectionName.split('.')[0].toUpperCase();
      // Only update sections that belong to the same component type (EMAIL or DASHBOARD)
      if (sectionComponentType === selectedProductType || section.sectionName.startsWith(selectedProductType)) {
        return {
          ...section,
          clusterResolutions: {
            ...section.clusterResolutions,
            [clusterReference]: 'pass'
          }
        };
      }
      return section;
    }));

    setHasChanges(true);
    handleAddClusterClose();
  };

  const handleRemoveCluster = (clusterReference: string) => {
    // Remove the cluster column
    setClusterColumns(prev => prev.filter(col => col !== clusterReference));
    
    // Update clustersPerProduct for ALL products of the same component type
    setClustersPerProduct(prev => {
      const updated = { ...prev };
      const componentType = selectedProductType;
      
      if (componentType === 'EMAIL') {
        // For EMAIL, extract the product key (without fuel type) from selectedFeature
        // selectedFeature format: EMAIL.SF.MONTHLY_SUMMARY.ELECTRIC or EMAIL.CDF.NEIGHBOURHOOD_COMPARISON (no fuel type)
        const parts = selectedFeature.split('.');
        const lastPart = parts[parts.length - 1];
        
        if (FUEL_TYPE_SUFFIXES.includes(lastPart)) {
          const productKeyWithoutFuel = parts.slice(0, -1).join('.');
          // Remove from all fuel-specific keys that match this product
          Object.keys(updated).forEach(productKey => {
            if (productKey.startsWith(productKeyWithoutFuel + '.')) {
              updated[productKey] = (updated[productKey] || []).filter(c => c !== clusterReference);
            }
          });
        } else {
          // No fuel type suffix (e.g., CDF products) - remove from matching keys directly
          Object.keys(updated).forEach(productKey => {
            if (productKey === selectedFeature || productKey.startsWith(selectedFeature + '.')) {
              updated[productKey] = (updated[productKey] || []).filter(c => c !== clusterReference);
            }
          });
        }
      } else if (componentType === 'DASHBOARD') {
        // For DASHBOARD, remove from all DASHBOARD fuel-specific keys
        Object.keys(updated).forEach(productKey => {
          if (productKey === 'DASHBOARD' || productKey.startsWith('DASHBOARD.')) {
            updated[productKey] = (updated[productKey] || []).filter(c => c !== clusterReference);
          }
        });
      } else {
        // For HER, update all products that match the component type
        Object.keys(updated).forEach(productKey => {
          const productPrefix = productKey.split('.')[0].toUpperCase();
          if (productPrefix === componentType || productKey === componentType) {
            updated[productKey] = (updated[productKey] || []).filter(c => c !== clusterReference);
          }
        });
      }
      
      return updated;
    });

    // Remove the cluster resolution from all sections of the same component type
    setSections(prev => prev.map(section => {
      const sectionComponentType = section.sectionName.split('.')[0].toUpperCase();
      if (sectionComponentType === selectedProductType || section.sectionName.startsWith(selectedProductType)) {
        const { [clusterReference]: removed, ...remainingResolutions } = section.clusterResolutions;
        return {
          ...section,
          clusterResolutions: remainingResolutions
        };
      }
      return section;
    }));

    setHasChanges(true);
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, sectionId: string) => {
    setDraggedItem(sectionId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, sectionId: string) => {
    e.preventDefault();
    if (draggedItem !== sectionId) {
      setDragOverItem(sectionId);
    }
  };

  const handleDragLeave = () => {
    setDragOverItem(null);
  };

  const handleDrop = (e: React.DragEvent, targetSectionId: string) => {
    e.preventDefault();
    
    if (!draggedItem || draggedItem === targetSectionId) {
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    setSections(prev => {
      const newSections = [...prev];
      const draggedIndex = newSections.findIndex(s => s.id === draggedItem);
      const targetIndex = newSections.findIndex(s => s.id === targetSectionId);
      
      if (draggedIndex === -1 || targetIndex === -1) return prev;

      const draggedSection = newSections[draggedIndex];
      const targetSection = newSections[targetIndex];
      
      // Get the product key based on component type
      // For DASHBOARD: use first part (DASHBOARD)
      // For EMAIL: use first 3 parts (EMAIL.SF.MONTHLY_SUMMARY)
      const getProductKey = (sectionName: string) => {
        const parts = sectionName.split('.');
        if (parts[0]?.toUpperCase() === 'DASHBOARD') {
          return 'DASHBOARD';
        }
        return parts.slice(0, 3).join('.');
      };
      
      const draggedProductKey = getProductKey(draggedSection.sectionName);
      const targetProductKey = getProductKey(targetSection.sectionName);
      
      // Only allow reordering within the same product
      if (draggedProductKey !== targetProductKey) {
        return prev;
      }

      // Remove dragged item and insert at target position
      const [removed] = newSections.splice(draggedIndex, 1);
      newSections.splice(targetIndex, 0, removed);

      // --- Dashboard feature drag: keep every fuel variant's order in sync ---
      // The dashboard view only shows/drags one fuel (e.g. ELECTRIC), but the
      // saved POSITION collapses all fuels of a widget onto its lowest-position
      // variant (see handleSaveChanges featurePositionMap). So a single-fuel drag
      // must be mirrored onto the other fuels of the same page, otherwise the
      // saved position never changes. Pages (which use per-fuel positions) and
      // EMAIL are unaffected and deliberately left on the simple splice path.
      const getFuel = (sectionName: string): string => {
        const parts = sectionName.split('.');
        const last = parts[parts.length - 1];
        return FUEL_TYPE_SUFFIXES.includes(last) ? last : '';
      };
      const isPage = (s: SectionItem): boolean =>
        !!s.mappedPage && getDashboardCoreName(s.sectionName) === s.mappedPage;

      const draggedFuel = getFuel(draggedSection.sectionName);
      const pageName = draggedSection.mappedPage;

      if (
        draggedProductKey === 'DASHBOARD' &&
        !isPage(draggedSection) &&
        draggedFuel &&
        pageName
      ) {
        // Only reorder features within the same page; ignore drops onto a page
        // row or onto a feature from another page (prevents silent snap-back).
        if (isPage(targetSection) || targetSection.mappedPage !== pageName) {
          return prev;
        }

        // Canonical widget order = the dragged (visible) fuel's post-splice order.
        const canonicalCores = newSections
          .filter(s => getProductKey(s.sectionName) === 'DASHBOARD'
            && !isPage(s)
            && s.mappedPage === pageName
            && getFuel(s.sectionName) === draggedFuel)
          .map(s => getDashboardCoreName(s.sectionName));
        const rankOf = new Map<string, number>();
        canonicalCores.forEach((core, i) => rankOf.set(core, i));

        const fuelRank: Record<string, number> = { ELECTRIC: 0, GAS: 1, WATER: 2 };
        const EXTRA = Number.MAX_SAFE_INTEGER;
        const coreRank = (name: string): number => {
          const core = getDashboardCoreName(name);
          return rankOf.has(core) ? rankOf.get(core)! : EXTRA;
        };

        // Slots occupied by this page's feature rows (all fuels), in array order.
        const slots: number[] = [];
        newSections.forEach((s, i) => {
          if (getProductKey(s.sectionName) === 'DASHBOARD'
            && !isPage(s)
            && s.mappedPage === pageName) {
            slots.push(i);
          }
        });

        // Reorder those rows by widget rank first, then fuel. Widgets that exist
        // only in another fuel (no rank in the dragged fuel) keep their relative
        // order at the end. Writing back into the same slots leaves pages and
        // other pages/products untouched.
        const reordered = slots
          .map(i => newSections[i])
          .sort((a, b) => {
            const ra = coreRank(a.sectionName);
            const rb = coreRank(b.sectionName);
            if (ra !== rb) return ra - rb;
            return (fuelRank[getFuel(a.sectionName)] ?? 99) - (fuelRank[getFuel(b.sectionName)] ?? 99);
          });
        slots.forEach((slotIdx, k) => { newSections[slotIdx] = reordered[k]; });
      }

      // Reassign positions only for this product (1, 2, 3, ...)
      let positionCounter = 1;
      return newSections.map(section => {
        if (getProductKey(section.sectionName) === draggedProductKey) {
          return { ...section, position: positionCounter++ };
        }
        return section;
      });
    });

    setDraggedItem(null);
    setDragOverItem(null);
    setHasChanges(true);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleAddSectionFromMaster = () => {
    if (!selectedMasterSection) return;
    
    // Check if it's from schema level2 options
    if (schemaLevel2Options.includes(selectedMasterSection)) {
      // Create a new section from the schema level2 option
      const newSection: SectionItem = {
        id: selectedMasterSection,
        sectionName: selectedMasterSection,
        position: sections.length + 1,
        mandatory: selectedMasterSection.includes('.TRUE.'),
        isRemoved: false,
        clusterResolutions: {
          '(Default)': 'access'
        }
      };
      
      // Add resolutions for existing clusters
      clusterColumns.forEach(clusterRef => {
        if (clusterRef !== '(Default)') {
          newSection.clusterResolutions[clusterRef] = 'pass';
        }
      });
      
      setSections(prev => [...prev, newSection]);
      
      // Track new EMAIL sections for push-new-sections API
      // Format: EMAIL.SF.MONTHLY_SUMMARY.TRUE.HEADER_V4.ELECTRIC
      // We need: event = MONTHLY_SUMMARY, section = HEADER_V4
      if (selectedMasterSection.startsWith('EMAIL')) {
        const parts = selectedMasterSection.split('.');
        // parts: [EMAIL, SF, MONTHLY_SUMMARY, ..., SECTION_NAME, TRUE/FALSE, FUEL_TYPE]
        // Section name is third last item
        if (parts.length >= 5) {
          const event = parts[2]; // e.g., MONTHLY_SUMMARY
          const sectionName = parts[parts.length - 3]; // e.g., HEADER_V4 (third last)
          
          setNewEmailSections(prev => {
            const updated = new Map(prev);
            if (!updated.has(event)) {
              updated.set(event, new Set());
            }
            updated.get(event)!.add(sectionName);
            return updated;
          });
        }
      }
      
      rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_TOOLS.TEMPLATE_RULE_CREATED, {
        product_type: selectedProductType,
        source: 'schema',
      });
      setSelectedMasterSection('');
      setAddSectionDialogOpen(false);
      setHasChanges(true);
      return;
    }

    // Fallback: check master section list
    const masterSection = masterSectionList.find(s => s.id === selectedMasterSection);
    if (masterSection && !sections.find(s => s.id === masterSection.id)) {
      setSections(prev => [...prev, { 
        ...masterSection, 
        position: prev.length + 1,
        isRemoved: false 
      }]);
      
      // Track new EMAIL sections for push-new-sections API
      if (masterSection.sectionName.startsWith('EMAIL')) {
        const parts = masterSection.sectionName.split('.');
        if (parts.length >= 5) {
          const event = parts[2];
          const sectionName = parts[parts.length - 3]; // Third last item
          
          setNewEmailSections(prev => {
            const updated = new Map(prev);
            if (!updated.has(event)) {
              updated.set(event, new Set());
            }
            updated.get(event)!.add(sectionName);
            return updated;
          });
        }
      }
      
      rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_TOOLS.TEMPLATE_RULE_CREATED, {
        product_type: selectedProductType,
        source: 'master',
      });
      setSelectedMasterSection('');
      setAddSectionDialogOpen(false);
      setHasChanges(true);
    }
  };

  // Filter out clusters that are already added as columns
  // Handle (Default) vs DEFAULT mismatch - clusterColumns uses (Default) but API returns DEFAULT
  const availableClusterOptions = availableClusters.filter(
    cluster => {
      const clusterRef = cluster.clusterReference;
      const isAlreadyAdded = clusterColumns.includes(clusterRef) || 
                            (clusterRef === 'DEFAULT' && clusterColumns.includes('(Default)'));
      return !isAlreadyAdded;
    }
  );

  // Filter sections by selected feature/product, sort by original position, then assign display positions
  const filteredSections = (selectedFeature 
    ? sections.filter(section => {
        // For HER, filter by product type prefix
        if (selectedFeature === 'HER') {
          return section.sectionName.startsWith(selectedFeature);
        }
        // For DASHBOARD with fuel type, filter by prefix AND fuel type suffix
        // selectedFeature format: DASHBOARD.ELECTRIC
        if (selectedFeature.startsWith('DASHBOARD.')) {
          const parts = selectedFeature.split('.');
          const fuelType = parts[parts.length - 1]; // Last part is fuel type
          // Check if section starts with DASHBOARD and ends with fuel type
          return section.sectionName.startsWith('DASHBOARD') && 
                 section.sectionName.endsWith(`.${fuelType}`);
        }
        // For EMAIL products, filter by product key prefix AND fuel type suffix
        // selectedFeature format: EMAIL.SF.MONTHLY_SUMMARY.ELECTRIC or EMAIL.CDF.NEIGHBOURHOOD_COMPARISON (no fuel type)
        if (selectedFeature.startsWith('EMAIL')) {
          const parts = selectedFeature.split('.');
          const lastPart = parts[parts.length - 1];
          
          if (FUEL_TYPE_SUFFIXES.includes(lastPart)) {
            // Has fuel type suffix: filter by product key prefix AND fuel type suffix
            const productKey = parts.slice(0, -1).join('.');
            return section.sectionName.startsWith(productKey) && 
                   section.sectionName.endsWith(`.${lastPart}`);
          } else {
            // No fuel type suffix (e.g., CDF products) - filter by prefix only
            return section.sectionName.startsWith(selectedFeature);
          }
        }
        return false;
      })
    : sections
  )
    .sort((a, b) => a.position - b.position) // Sort by original API position
    .map((section, index) => ({
      ...section,
      position: index + 1, // Assign sequential display position starting from 1
    }));

  // Extract fuel types present in the filtered sections (what's shown in the table)
  const tableFuelTypes = new Set<string>();
  filteredSections.forEach(section => {
    const parts = section.sectionName.split('.');
    const fuelType = parts[parts.length - 1]; // Last part is fuel type (ELECTRIC, GAS, WATER)
    if (FUEL_TYPE_SUFFIXES.includes(fuelType)) {
      tableFuelTypes.add(fuelType);
    }
  });

  // Helper: extract the core name from a dashboard sectionName
  // e.g. "DASHBOARD.ENERGY_INSIGHTS.ELECTRIC" → "ENERGY_INSIGHTS"
  // e.g. "DASHBOARD.ENERGY_INSIGHTS_USAGE_MODE_PAGE.USAGE.APPLIANCE_VIEW.MONTH.ELECTRIC" → "ENERGY_INSIGHTS_USAGE_MODE_PAGE.USAGE.APPLIANCE_VIEW.MONTH"
  const getDashboardCoreName = (sectionName: string): string => {
    const parts = sectionName.split('.');
    const withoutPrefix = parts.slice(1); // remove "DASHBOARD"
    const lastPart = withoutPrefix[withoutPrefix.length - 1];
    if (FUEL_TYPE_SUFFIXES.includes(lastPart)) {
      return withoutPrefix.slice(0, -1).join('.');
    }
    return withoutPrefix.join('.');
  };

  // Check if a dashboard section IS a page (its core name equals its own mappedPage)
  const isDashboardPage = (section: typeof filteredSections[0]): boolean => {
    if (!section.mappedPage) return false;
    const coreName = getDashboardCoreName(section.sectionName);
    return coreName === section.mappedPage;
  };

  // Group dashboard sections: pages (with own sequence) containing features (with own per-page sequence)
  type DashboardSectionItem = typeof filteredSections[0];
  const dashboardPageGroups: {
    pageName: string;
    pageSection: DashboardSectionItem;
    pagePosition: number;
    features: (DashboardSectionItem & { featurePosition: number })[];
  }[] = (() => {
    if (!isDashboard) return [];

    // Separate page-level items from feature-level items
    const pageItems: DashboardSectionItem[] = [];
    const featureItems: DashboardSectionItem[] = [];

    filteredSections.forEach(section => {
      if (isDashboardPage(section)) {
        pageItems.push(section);
      } else {
        featureItems.push(section);
      }
    });

    // Sort pages by their original position, then assign page sequence 1..N
    pageItems.sort((a, b) => a.position - b.position);

    const groups = pageItems.map((pageSection, pageIndex) => {
      const pageName = pageSection.mappedPage || getDashboardCoreName(pageSection.sectionName);

      // Find features that belong to this page (their mappedPage matches this page's mappedPage)
      const pageFeatures = featureItems
        .filter(f => f.mappedPage === pageName)
        .sort((a, b) => a.position - b.position)
        .map((f, featureIndex) => ({
          ...f,
          featurePosition: featureIndex + 1,
        }));

      return {
        pageName,
        pageSection: { ...pageSection, position: pageIndex + 1 },
        pagePosition: pageIndex + 1,
        features: pageFeatures,
      };
    });

    // Gather features without a matching page into an "Other" group
    const allPageNames = new Set(pageItems.map(p => p.mappedPage || getDashboardCoreName(p.sectionName)));
    const orphanFeatures = featureItems
      .filter(f => !allPageNames.has(f.mappedPage || ''))
      .sort((a, b) => a.position - b.position)
      .map((f, featureIndex) => ({
        ...f,
        featurePosition: featureIndex + 1,
      }));

    if (orphanFeatures.length > 0) {
      // Create a synthetic page section for "Other"
      const syntheticPage: DashboardSectionItem = {
        id: '__OTHER_PAGE__',
        sectionName: 'Other',
        position: groups.length + 1,
        mandatory: false,
        isRemoved: false,
        clusterResolutions: {},
        mappedPage: 'Other',
      };
      groups.push({
        pageName: 'Other',
        pageSection: syntheticPage,
        pagePosition: groups.length + 1,
        features: orphanFeatures,
      });
    }

    return groups;
  })();

  // Helper to format page name for display (e.g. ENERGY_INSIGHTS -> Energy Insights)
  const formatPageName = (page: string): string => {
    return page
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Get sections not currently in the table from schema level2 options
  // Filter by selected product/feature prefix, fuel type, and exclude sections already in table
  const availableOverrideSections = schemaLevel2Options.filter(option => {
    // First check if option is already in the filtered sections (what's shown in the table)
    if (filteredSections.find(s => s.id === option || s.sectionName === option)) {
      return false;
    }
    
    // Filter by selected product
    if (selectedFeature) {
      // For EMAIL products, filter by product key prefix AND fuel type suffix
      // selectedFeature format: EMAIL.SF.MONTHLY_SUMMARY.ELECTRIC or EMAIL.CDF.NEIGHBOURHOOD_COMPARISON (no fuel type)
      if (selectedFeature.startsWith('EMAIL')) {
        const parts = selectedFeature.split('.');
        const lastPart = parts[parts.length - 1];
        
        if (FUEL_TYPE_SUFFIXES.includes(lastPart)) {
          const productKey = parts.slice(0, -1).join('.'); // Everything except last part
          // Check if option starts with product key and ends with the same fuel type
          if (!option.startsWith(productKey) || !option.endsWith(`.${lastPart}`)) {
            return false;
          }
        } else {
          // No fuel type suffix (e.g., CDF products) - filter by prefix only
          if (!option.startsWith(selectedFeature)) {
            return false;
          }
        }
        return true;
      }
      // For DASHBOARD with fuel type, filter by prefix AND fuel type suffix
      // selectedFeature format: DASHBOARD.ELECTRIC
      if (selectedFeature.startsWith('DASHBOARD.')) {
        const parts = selectedFeature.split('.');
        const fuelType = parts[parts.length - 1]; // Last part is fuel type
        
        // Check if option starts with DASHBOARD and ends with the same fuel type
        if (!option.startsWith('DASHBOARD') || !option.endsWith(`.${fuelType}`)) {
          return false;
        }
        return true;
      }
      // For HER, filter by product type prefix
      return option.startsWith(selectedFeature);
    }
    
    return true;
  });

  // Get sections not currently in the table (legacy - for fallback)
  const availableMasterSections = masterSectionList.filter(
    ms => !filteredSections.find(s => s.id === ms.id)
  );

  return (
    <Box sx={{ 
      minHeight: '100vh',
      bgcolor: 'background.default',
      p: 3,
    }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Template Personalization
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ArrowLeft size={16} />}
            onClick={handleBack}
            sx={{ color: 'text.secondary', borderColor: 'divider' }}
          >
            Back to Cluster Configuration
          </Button>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ mx: 'auto' }}>
        <StyledCard>
          <CardContent sx={{ p: 3, textAlign: 'left' }}>
            {/* Template Configuration Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: .5 }}>
                  <Lock size={24} style={{ marginRight: 12, color: theme.palette.primary.main }} />
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    Template Configuration
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Define and apply template configurations to clusters.
                </Typography>
              </Box>
              <Button
                variant="outlined"
                startIcon={<Download size={16} />}
                onClick={() => {
                  setImportSourceEnvId(importSourceOptions[0] ? String(importSourceOptions[0].id) : '');
                  setImportSelectedEvents([]);
                  setImportOpen(true);
                }}
                disabled={importSourceOptions.length === 0 && unmappedSourceAppEnvs.length === 0}
                sx={{ color: 'text.secondary', borderColor: 'divider' }}
              >
                Import Events
              </Button>
            </Box>

            {/* Filter */}
            <Box sx={{ mb: 3 }}>
              <FormControl size="small" sx={{ minWidth: 350 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', marginBottom: .5 }}>
                  Filter by Feature
                </Typography>
                <Select
                  value={selectedFeature}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedFeature(value);
                    // Find the product type for this selection
                    const option = productTypeOptions.find(opt => opt.value === value);
                    if (option) {
                      setSelectedProductType(option.productType);
                    }
                    // Update cluster columns for the selected product
                    const productClusters = clustersPerProduct[value] || ['(Default)'];
                    setClusterColumns(productClusters);
                  }}
                >
                  {productTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{p: 3, border: `1px dashed ${theme.palette.divider}`, borderRadius: 2 }}>

            {/* Template Section */}
            {selectedFeature && (
              <Box sx={{ mb: 3}}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BarChart3 size={20} style={{ color: theme.palette.primary.main }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                      {isDashboard ? 'Dashboard Template' : isHER ? 'HER Template' : 'Email Template for:'}{' '}
                      {!isDashboard && !isHER && (
                        <Typography component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>
                          {getFeatureDisplayName(selectedFeature)}
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {!isDashboard && !isHER && (
                      <Button
                        variant="outlined"
                        startIcon={<BarChart3 size={16} />}
                        onClick={() => setAddSectionDialogOpen(true)}
                        sx={{ color: 'text.secondary', borderColor: 'divider' }}
                      >
                        Add New Section
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      startIcon={<Plus size={16} />}
                      endIcon={<ChevronDown size={16} />}
                      onClick={handleAddClusterClick}
                      sx={{ color: 'text.secondary', borderColor: 'divider' }}
                    >
                      Add Cluster
                    </Button>
                    <Menu
                      id="add-cluster-menu"
                      anchorEl={addClusterAnchor}
                      open={addClusterOpen}
                      onClose={handleAddClusterClose}
                      PaperProps={{
                        sx: {
                          borderRadius: 2,
                          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
                          border: '1px solid #E0E0E0',
                          minWidth: '160px',
                        },
                      }}
                    >
                      {availableClusterOptions.length > 0 ? (
                        availableClusterOptions.map((cluster) => (
                          <MenuItem 
                            key={cluster.clusterId || cluster.clusterReference} 
                            onClick={() => handleClusterTypeSelect(cluster.clusterReference)}
                          >
                            {cluster.clusterReference || cluster.clusterName}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem disabled>
                          {availableClusters.length === 0 ? 'Loading clusters...' : 'All clusters added'}
                        </MenuItem>
                      )}
                    </Menu>
                  </Box>
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                  {isHER 
                    ? 'HER template configuration is available in the next update.' 
                    : isDashboard 
                      ? 'This table shows the dashboard sections and their status across different clusters.'
                      : 'This table shows the email sections and their status across different clusters.'
                  }
                </Typography>
              </Box>
            )}

            {/* Template Sections Table */}
            {loading ? (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                py: 8,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                bgcolor: 'background.default'
              }}>
                <CircularProgress size={40} />
                <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
                  Loading template data...
                </Typography>
              </Box>
            ) : isHER ? (
              <Box sx={{ 
                textAlign: 'center', 
                py: 8, 
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                bgcolor: 'background.default'
              }}>
                <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
                  Available in next update
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  HER template personalization will be available in the next update.
                </Typography>
              </Box>
            ) : filteredSections.length === 0 ? (
              <Box sx={{ 
                textAlign: 'center', 
                py: 8, 
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                bgcolor: 'background.default'
              }}>
                <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
                  No sections found
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  No template sections available for this configuration.
                </Typography>
              </Box>
            ) : isDashboard && dashboardPageGroups.length > 0 ? (
                <div style={{
                  borderRadius: 8,
                  border: `1px solid ${theme.palette.divider}`,
                  overflow: 'auto',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }}>
                <div style={{ width: 'fit-content', minWidth: '100%' }}>
                  {/* ── PAGES section ── */}
                  <div style={{
                    padding: '10px 16px',
                    backgroundColor: theme.palette.mode === 'dark' ? '#14365c' : '#e8eef5',
                    borderBottom: `2px solid ${theme.palette.mode === 'dark' ? '#008080' : '#003366'}`,
                    textAlign: 'left',
                    color: theme.palette.mode === 'dark' ? '#d6deeb' : '#003366',
                    fontSize: 13,
                    fontWeight: 700,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase' as const,
                  }}>
                    Pages
                  </div>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'background.default', '& .MuiTableCell-head': { position: 'sticky', top: 0, zIndex: 2, backgroundColor: theme.palette.background.default } }}>
                        <TableCell sx={{ fontWeight: 600, width: 60 }}>Move</TableCell>
                        <TableCell sx={{ fontWeight: 600, width: 60 }}>Pos</TableCell>
                        <TableCell sx={{ fontWeight: 600, minWidth: 240, whiteSpace: 'nowrap' }}>Feature</TableCell>
                        {clusterColumns.map((clusterRef, index) => {
                          const isDefaultCluster = index === 0 || clusterRef === '(Default)';
                          return (
                            <TableCell key={clusterRef} align="center" sx={{ fontWeight: 600, '&:hover .remove-column-btn': { opacity: 1 } }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                {clusterRef}
                                {!isDefaultCluster && (
                                  <Tooltip title="Remove column">
                                    <IconButton className="remove-column-btn" size="small" onClick={() => handleRemoveCluster(clusterRef)}
                                      sx={{ opacity: 0, transition: 'opacity 0.2s', p: 0.5, bgcolor: 'primary.main', color: 'error.main', '&:hover': { bgcolor: 'primary.dark' } }}>
                                      <X size={14} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dashboardPageGroups
                        .filter(g => g.pageSection.id !== '__OTHER_PAGE__')
                        .map((group) => (
                          <TableRow
                            key={`pages-${group.pageSection.id}`}
                            draggable
                            onDragStart={(e) => handleDragStart(e, group.pageSection.id)}
                            onDragOver={(e) => handleDragOver(e, group.pageSection.id)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, group.pageSection.id)}
                            onDragEnd={handleDragEnd}
                            sx={{
                              opacity: draggedItem === group.pageSection.id ? 0.5 : 1,
                              backgroundColor: dragOverItem === group.pageSection.id
                                ? 'action.hover'
                                : theme.palette.mode === 'dark' ? 'rgba(144, 202, 249, 0.06)' : 'rgba(25, 118, 210, 0.03)',
                              transition: 'background-color 0.2s',
                              ...(clusterColumns.some((clusterRef) => isCellStaged(group.pageSection.id, clusterRef)) && {
                                bgcolor: 'rgba(147,51,234,0.04)',
                                outline: '1px solid #9333ea',
                                outlineOffset: '-1px',
                              }),
                            }}
                          >
                            <TableCell sx={{ px: 1, py: 0.5 }}>
                              <IconButton size="small" sx={{ cursor: 'grab', color: 'text.secondary', '&:active': { cursor: 'grabbing' } }}>
                                <DragIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                            <TableCell sx={{ px: 1, py: 0.5 }}>
                              <Typography sx={{ fontSize: '12px', textAlign: 'center', fontWeight: 700, color: 'primary.main' }}>
                                {group.pagePosition}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ px: 1, py: 0.5 }}>
                              <Tooltip title={group.pageSection.sectionName} placement="top" arrow>
                                <Typography sx={{ fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                  {sectionLabel(group.pageSection)}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            {clusterColumns.map((clusterRef) => {
                              const isDefaultCluster = clusterRef === '(Default)';
                              let resolution = group.pageSection.clusterResolutions[clusterRef] || (isDefaultCluster ? 'access' : 'pass');
                              if (isDefaultCluster && resolution === 'pass') resolution = 'access';
                              return (
                                <TableCell key={clusterRef} align="center" sx={{ px: 1, py: 0.5 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                    <AccessButton status={resolution} staged={isCellStaged(group.pageSection.id, clusterRef)} onClick={(e) => handleDropdownOpen(e, group.pageSection.id, clusterRef)}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        {getStatusIcon(resolution)}
                                        {resolution.toUpperCase()}
                                      </Box>
                                      <ChevronDown size={14} />
                                    </AccessButton>
                                  </Box>
                                  <AccessStatusDropdown
                                    status={resolution}
                                    anchorEl={dropdownAnchor[`${group.pageSection.id}-${clusterRef}`]}
                                    onClose={() => handleDropdownClose(group.pageSection.id, clusterRef)}
                                    onSelect={(newStatus) => handleAccessChange(group.pageSection.id, clusterRef, newStatus)}
                                    allowedStatuses={isDefaultCluster ? ['access', 'kill'] : undefined}
                                  />
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>

                  {/* ── Per-page feature sections ── */}
                  {dashboardPageGroups.map((group) => {
                    if (group.features.length === 0) return null;
                    const isOtherGroup = group.pageSection.id === '__OTHER_PAGE__';
                    return (
                      <React.Fragment key={`features-${group.pageName}`}>
                        {/* Page name header - plain div */}
                        <div style={{
                          padding: '10px 16px',
                          backgroundColor: theme.palette.mode === 'dark' ? '#14365c' : '#e8eef5',
                          borderTop: `1px solid ${theme.palette.divider}`,
                          borderBottom: `2px solid ${theme.palette.mode === 'dark' ? '#008080' : '#003366'}`,
                          textAlign: 'left',
                          color: theme.palette.mode === 'dark' ? '#d6deeb' : '#003366',
                          fontSize: 13,
                          fontWeight: 700,
                          letterSpacing: '0.05em',
                          textTransform: 'uppercase' as const,
                        }}>
                          {formatPageName(group.pageName)}
                        </div>
                        {/* Feature rows table */}
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ backgroundColor: 'background.default', '& .MuiTableCell-head': { position: 'sticky', top: 0, zIndex: 2, backgroundColor: theme.palette.background.default } }}>
                              <TableCell sx={{ fontWeight: 600, width: 60 }}>Move</TableCell>
                              <TableCell sx={{ fontWeight: 600, width: 60 }}>Pos</TableCell>
                              <TableCell sx={{ fontWeight: 600, minWidth: 240, whiteSpace: 'nowrap' }}>Feature</TableCell>
                              {clusterColumns.map((clusterRef, index) => {
                                const isDefaultCluster = index === 0 || clusterRef === '(Default)';
                                return (
                                  <TableCell key={clusterRef} align="center" sx={{ fontWeight: 600, '&:hover .remove-column-btn': { opacity: 1 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                      {clusterRef}
                                      {!isDefaultCluster && (
                                        <Tooltip title="Remove column">
                                          <IconButton className="remove-column-btn" size="small" onClick={() => handleRemoveCluster(clusterRef)}
                                            sx={{ opacity: 0, transition: 'opacity 0.2s', p: 0.5, bgcolor: 'primary.main', color: 'error.main', '&:hover': { bgcolor: 'primary.dark' } }}>
                                            <X size={14} />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </Box>
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {group.features.map((feature) => (
                              <TableRow
                                key={feature.id}
                                hover
                                draggable
                                onDragStart={(e) => handleDragStart(e, feature.id)}
                                onDragOver={(e) => handleDragOver(e, feature.id)}
                                onDragLeave={handleDragLeave}
                                onDrop={(e) => handleDrop(e, feature.id)}
                                onDragEnd={handleDragEnd}
                                sx={{
                                  opacity: draggedItem === feature.id ? 0.5 : 1,
                                  backgroundColor: dragOverItem === feature.id ? 'action.hover' : 'inherit',
                                  transition: 'background-color 0.2s',
                                  ...(clusterColumns.some((clusterRef) => isCellStaged(feature.id, clusterRef)) && {
                                    bgcolor: 'rgba(147,51,234,0.04)',
                                    outline: '1px solid #9333ea',
                                    outlineOffset: '-1px',
                                  }),
                                }}
                              >
                                <TableCell sx={{ px: 1, py: 0.5, width: 60 }}>
                                  <IconButton size="small" sx={{ cursor: 'grab', color: 'text.secondary', '&:active': { cursor: 'grabbing' } }}>
                                    <DragIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                                <TableCell sx={{ px: 1, py: 0.5, width: 60 }}>
                                  <Typography sx={{ fontSize: '12px', textAlign: 'center' }}>
                                    {feature.featurePosition}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ px: 1, py: 0.5 }}>
                                  <Tooltip title={feature.sectionName} placement="top" arrow>
                                    <Typography sx={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                                      {sectionLabel(feature)}
                                    </Typography>
                                  </Tooltip>
                                </TableCell>
                                {clusterColumns.map((clusterRef) => {
                                  const isDefaultCluster = clusterRef === '(Default)';
                                  let resolution = feature.clusterResolutions[clusterRef] || (isDefaultCluster ? 'access' : 'pass');
                                  if (isDefaultCluster && resolution === 'pass') resolution = 'access';
                                  return (
                                    <TableCell key={clusterRef} align="center" sx={{ px: 1, py: 0.5 }}>
                                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                        <AccessButton status={resolution} staged={isCellStaged(feature.id, clusterRef)} onClick={(e) => handleDropdownOpen(e, feature.id, clusterRef)}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            {getStatusIcon(resolution)}
                                            {resolution.toUpperCase()}
                                          </Box>
                                          <ChevronDown size={14} />
                                        </AccessButton>
                                      </Box>
                                      <AccessStatusDropdown
                                        status={resolution}
                                        anchorEl={dropdownAnchor[`${feature.id}-${clusterRef}`]}
                                        onClose={() => handleDropdownClose(feature.id, clusterRef)}
                                        onSelect={(newStatus) => handleAccessChange(feature.id, clusterRef, newStatus)}
                                        allowedStatuses={isDefaultCluster ? ['access', 'kill'] : undefined}
                                      />
                                    </TableCell>
                                  );
                                })}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </React.Fragment>
                    );
                  })}
                </div>
                </div>
              ) : (
              /* ──────── Non-dashboard (Email/HER): single table ──────── */
              <TableContainer component={Paper} sx={{ 
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                border: `1px solid ${theme.palette.divider}`,
                overflowX: 'auto',
              }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'background.default' }}>
                      <TableCell sx={{ fontWeight: 600, width: 60 }}>Move</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 80 }}>Remove</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 100 }}>Mandatory</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 60 }}>Pos</TableCell>
                      <TableCell sx={{ fontWeight: 600, minWidth: 240, whiteSpace: 'nowrap' }}>Section Name</TableCell>
                      {clusterColumns.map((clusterRef, index) => {
                        const isDefaultCluster = index === 0 || clusterRef === '(Default)';
                        return (
                          <TableCell 
                            key={clusterRef} 
                            align="center" 
                            sx={{ 
                              fontWeight: 600,
                              '&:hover .remove-column-btn': {
                                opacity: 1,
                              },
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              {clusterRef}
                              {!isDefaultCluster && (
                                <Tooltip title="Remove column">
                                  <IconButton
                                    className="remove-column-btn"
                                    size="small"
                                    onClick={() => handleRemoveCluster(clusterRef)}
                                    sx={{
                                      opacity: 0,
                                      transition: 'opacity 0.2s',
                                      p: 0.5,
                                      bgcolor: 'primary.main',
                                      color: 'error.main',
                                      '&:hover': {
                                        bgcolor: 'primary.dark',
                                      },
                                    }}
                                  >
                                    <X size={14} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredSections.map((section) => (
                      <TableRow 
                        key={section.id} 
                        hover
                        draggable
                        onDragStart={(e) => handleDragStart(e, section.id)}
                        onDragOver={(e) => handleDragOver(e, section.id)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, section.id)}
                        onDragEnd={handleDragEnd}
                        sx={{
                          opacity: draggedItem === section.id ? 0.5 : 1,
                          backgroundColor: dragOverItem === section.id ? 'action.hover' : 'inherit',
                          transition: 'background-color 0.2s',
                          ...(clusterColumns.some((clusterRef) => isCellStaged(section.id, clusterRef)) && {
                            bgcolor: 'rgba(147,51,234,0.04)',
                            outline: '1px solid #9333ea',
                            outlineOffset: '-1px',
                          }),
                        }}
                      >
                        <TableCell sx={{ px: 1, py: 0.5 }}>
                          <IconButton 
                            size="small" 
                            sx={{ cursor: 'grab', color: 'text.secondary', '&:active': { cursor: 'grabbing' } }}
                          >
                            <DragIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                          <TableCell sx={{ px: 1, py: 0.5 }}>
                            <IconButton 
                              size="small" 
                              onClick={() => handleRemoveSection(section.id)}
                              sx={{ color: 'error.main' }}
                            >
                              <X size={16} />
                            </IconButton>
                          </TableCell>
                          <TableCell sx={{ px: 1, py: 0.5 }}>
                            <Switch
                              checked={section.mandatory}
                              onChange={() => handleMandatoryToggle(section.id)}
                              size="small"
                              sx={{
                                width: 38,
                                height: 22,
                                padding: 0,
                                '& .MuiSwitch-switchBase': {
                                  padding: 0,
                                  margin: '2px',
                                  transitionDuration: '300ms',
                                  '&.Mui-checked': {
                                    transform: 'translateX(16px)',
                                    color: '#fff',
                                    '& + .MuiSwitch-track': {
                                      backgroundColor: theme.palette.primary.main,
                                      opacity: 1,
                                      border: 0,
                                    },
                                    '& .MuiSwitch-thumb': {
                                      color: '#fff',
                                    },
                                  },
                                  '&.Mui-focusVisible .MuiSwitch-thumb': {
                                    color: theme.palette.primary.main,
                                    border: '6px solid #fff',
                                  },
                                },
                                '& .MuiSwitch-thumb': {
                                  boxSizing: 'border-box',
                                  width: 18,
                                  height: 18,
                                  color: '#fff',
                                },
                                '& .MuiSwitch-track': {
                                  borderRadius: 22 / 2,
                                backgroundColor: theme.palette.mode === 'dark' 
                                  ? theme.palette.grey[700] 
                                  : theme.palette.grey[300],
                                opacity: 1,
                                transition: theme.transitions.create(['background-color'], {
                                  duration: 500,
                                }),
                              },
                              }}
                            />
                          </TableCell>
                        <TableCell sx={{ px: 1, py: 0.5 }}>
                          <Typography sx={{ fontSize: '12px', textAlign: 'center' }}>
                            {section.position}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ px: 1, py: 0.5 }}>
                          <Tooltip title={section.sectionName} placement="top" arrow>
                            <Typography sx={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                              {sectionLabel(section)}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        {clusterColumns.map((clusterRef) => {
                          const isDefaultCluster = clusterRef === '(Default)';
                          let resolution = section.clusterResolutions[clusterRef] || (isDefaultCluster ? 'access' : 'pass');
                          if (isDefaultCluster && resolution === 'pass') {
                            resolution = 'access';
                          }
                          return (
                            <TableCell key={clusterRef} align="center" sx={{ px: 1, py: 0.5 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <AccessButton
                                  status={resolution}
                                  staged={isCellStaged(section.id, clusterRef)}
                                  onClick={(e) => handleDropdownOpen(e, section.id, clusterRef)}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: .5 }}>
                                    {getStatusIcon(resolution)}
                                    {resolution.toUpperCase()}
                                  </Box>
                                  <ChevronDown size={14} />
                                </AccessButton>
                              </Box>
                              <AccessStatusDropdown
                                status={resolution}
                                anchorEl={dropdownAnchor[`${section.id}-${clusterRef}`]}
                                onClose={() => handleDropdownClose(section.id, clusterRef)}
                                onSelect={(newStatus) => handleAccessChange(section.id, clusterRef, newStatus)}
                                allowedStatuses={isDefaultCluster ? ['access', 'kill'] : undefined}
                              />
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Add Removed Section */}
            {removedSections.length > 0 && (
              <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Add Removed Section
                </Typography>
                <FormControl size="small" sx={{ minWidth: 300 }}>
                  <Select
                    value={selectedRemovedSection}
                    onChange={(e) => setSelectedRemovedSection(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      Select a section to re-add...
                    </MenuItem>
                    {removedSections.map((section) => (
                      <MenuItem key={section.id} value={section.id}>
                        {sectionLabel(section)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  startIcon={<Plus size={16} />}
                  onClick={handleReAddSection}
                  disabled={!selectedRemovedSection}
                  sx={{ color: 'primary.main', borderColor: 'primary.main' }}
                >
                  Re-add
                </Button>
              </Box>
            )}

            </Box>

            {/* Save Changes / Stage Changes Button — nonprod/prod targets can't be written to
                directly, so they stage instead (see handleStageChanges). */}
            {hasChanges && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <PermissionGate require={UI_PAGES.TEMPLATE_PERSONALIZATION.ctas.feature_resolution.id} mode="disable">
                  {isStageOnlyEnv ? (
                    <BglPrimaryButton
                      startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Layers size={16} />}
                      onClick={handleStageChanges}
                      disabled={saving}
                    >
                      {saving ? 'Staging...' : 'Stage Changes'}
                    </BglPrimaryButton>
                  ) : (
                    <BglPrimaryButton
                      startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                      onClick={handleSaveChanges}
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </BglPrimaryButton>
                  )}
                </PermissionGate>
              </Box>
            )}
          </CardContent>
        </StyledCard>
      </Box>

      {/* Import Events — pulls selected L1 events' L2 rows from another environment into this one. */}
      <Dialog open={importOpen} onClose={() => !isImporting && setImportOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Download size={20} color={theme.palette.primary.main} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Import Events</Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            Pull selected events' template composition from another environment into {currentEnv ? envLabel(currentEnv) : 'this environment'}.
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography
            sx={{ fontSize: 11, fontWeight: 900, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'text.secondary', mb: 1 }}
          >
            Source Environment
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 3 }}>
            <Select
              value={importSourceEnvId}
              onChange={(e) => {
                setImportSourceEnvId(e.target.value);
                setImportSelectedEvents([]); // the list is per source env — old picks may not exist there
              }}
              disabled={isImporting}
              sx={{ borderRadius: 1.5 }}
            >
              {importSourceOptions.map((env) => (
                <MenuItem key={String(env.id)} value={String(env.id)}>
                  {envLabel(env)}
                </MenuItem>
              ))}
              {unmappedSourceAppEnvs.map((appEnv) => (
                <MenuItem key={`unmapped-${appEnv}`} value={`unmapped-${appEnv}`} disabled>
                  {formatAppEnvLabel(appEnv)} — not mapped to this pilot
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography
            sx={{ fontSize: 11, fontWeight: 900, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'text.secondary', mb: 1 }}
          >
            Events to Import
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              multiple
              displayEmpty
              value={importSelectedEvents}
              onChange={(e) => {
                const value = e.target.value;
                setImportSelectedEvents(typeof value === 'string' ? value.split(',') : value);
              }}
              disabled={isImporting || loadingSourceEvents || importableEvents.length === 0}
              renderValue={(selected) => {
                const values = selected as string[];
                if (loadingSourceEvents) return <span style={{ opacity: 0.6 }}>Loading events…</span>;
                if (importableEvents.length === 0) {
                  return <span style={{ opacity: 0.6 }}>No importable events at this source</span>;
                }
                if (values.length === 0) return <span style={{ opacity: 0.6 }}>Select events…</span>;
                return values.map(importEventLabel).join(', ');
              }}
              sx={{ borderRadius: 1.5 }}
            >
              {importableEvents.map((event) => (
                <MenuItem key={event.value} value={event.value}>
                  <Checkbox checked={importSelectedEvents.includes(event.value)} size="small" />
                  <ListItemText primary={event.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              mt: 3,
              p: 2,
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: alpha(theme.palette.warning.main, 0.3),
              bgcolor: alpha(theme.palette.warning.main, 0.08),
            }}
          >
            <AlertTriangle size={16} color={theme.palette.warning.main} style={{ flexShrink: 0, marginTop: 2 }} />
            <Typography sx={{ fontSize: 12, lineHeight: 1.6, color: 'text.secondary' }}>
              Only the selected events' rows are replaced in {currentEnv ? envLabel(currentEnv) : 'this environment'} —
              everything else here is left as-is. For a nonprod/prod environment this stages the change
              instead of writing it directly.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="outlined" disabled={isImporting} onClick={() => setImportOpen(false)} sx={{ color: 'text.secondary', borderColor: 'divider' }}>
            Cancel
          </Button>
          <BglPrimaryButton onClick={handleImportEvents} disabled={isImporting || !importSourceEnvId || importSelectedEvents.length === 0}>
            {isImporting ? (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={14} sx={{ color: 'inherit' }} /> Importing...
              </Box>
            ) : (
              'Confirm & Import'
            )}
          </BglPrimaryButton>
        </DialogActions>
      </Dialog>

      {/* Add Override Section Dialog */}
      <Dialog
        open={addSectionDialogOpen}
        onClose={() => setAddSectionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Add Override Section
          </Typography>
          <IconButton onClick={() => setAddSectionDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            Select a section from the email level2 list to add it to the current template configuration.
          </Typography>
          <FormControl fullWidth>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
              Override Section
            </Typography>
            <Select
              value={selectedMasterSection}
              onChange={(e) => setSelectedMasterSection(e.target.value)}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 350,
                    '& .MuiMenuItem-root': {
                      fontSize: 12,
                    },
                  },
                },
              }}
            >
              <MenuItem value="" disabled sx={{ fontSize: 14 }}>
                Select a section to add as an override...
              </MenuItem>
              {availableOverrideSections.length > 0 ? (
                availableOverrideSections.map((sectionName) => (
                  <MenuItem key={sectionName} value={sectionName} sx={{ fontSize: 14 }}>
                    {sectionName}
                  </MenuItem>
                ))
              ) : (
                availableMasterSections.map((section) => (
                  <MenuItem key={section.id} value={section.id} sx={{ fontSize: 14 }}>
                    {sectionLabel(section)}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setAddSectionDialogOpen(false)} sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <PermissionGate require={UI_PAGES.TEMPLATE_PERSONALIZATION.ctas.push.id} mode="disable">
            <Button
              variant="contained"
              startIcon={<Plus size={16} />}
              onClick={handleAddSectionFromMaster}
              disabled={!selectedMasterSection}
            >
              Add Section
            </Button>
          </PermissionGate>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
